import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/Layout';
import { orders as ordersApi, delivery, drivers } from '@/lib/api';
import api from '@/lib/api';
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  DndContext, DragOverlay, closestCorners, 
  PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors, useDroppable 
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  RefreshCw, Truck, Building2, Bell, BellOff, UserPlus, Send, Eye, 
  CheckCircle, MapPin, Phone, Package, Clock, AlertCircle, List, LayoutGrid, Edit3, Plus, Minus, Trash2, Search, Tag, XCircle, Loader2, CreditCard, DollarSign, FileText, Banknote, Printer
} from 'lucide-react';
import { useNewOrderNotification } from '@/hooks/useNewOrderNotification';
import { thermalPrinter } from '@/services/thermalPrinter';

// Configuração de canais de entrega
const CHANNEL_CONFIG = {
  'IFOOD': { label: 'iFood', color: 'bg-red-100 text-red-700', icon: Truck },
  '99': { label: '99', color: 'bg-yellow-100 text-yellow-700', icon: Truck },
  '99FOOD': { label: '99Food', color: 'bg-yellow-100 text-yellow-700', icon: Truck },
  'KEETA': { label: 'Keeta', color: 'bg-purple-100 text-purple-700', icon: Truck },
  'RAPPI': { label: 'Rappi', color: 'bg-orange-100 text-orange-700', icon: Truck },
  'UBER_EATS': { label: 'Uber Eats', color: 'bg-green-100 text-green-700', icon: Truck },
  'LOJA': { label: 'Entrega Própria', color: 'bg-blue-100 text-blue-700', icon: Truck },
  'SALAO': { label: 'Salão', color: 'bg-slate-100 text-slate-700', icon: Building2 },
  'PDV': { label: 'Balcão', color: 'bg-slate-100 text-slate-700', icon: Building2 },
  'CARDAPIO_DIGITAL': { label: 'QRpedir', color: 'bg-indigo-100 text-indigo-700', icon: Package },
  'DELIVERY': { label: 'Delivery', color: 'bg-teal-100 text-teal-700', icon: Truck },
  'COMANDA': { label: 'Comanda', color: 'bg-amber-100 text-amber-700', icon: Package },
  'APP': { label: 'App', color: 'bg-cyan-100 text-cyan-700', icon: Package },
};

// Canais que são entregas parceiras (externas)
const PARTNER_CHANNELS = ['IFOOD', '99', '99FOOD', 'KEETA', 'RAPPI', 'UBER_EATS'];

// Colunas do Kanban - Fluxo completo incluindo "Pronto" para aguardar entregador
const KANBAN_COLUMNS = [
  { id: 'PENDENTE', label: 'Pendentes', color: 'bg-amber-500', bgColor: 'bg-amber-50' },
  { id: 'CONFIRMADO', label: 'Confirmados', color: 'bg-blue-500', bgColor: 'bg-blue-50' },
  { id: 'PREPARO', label: 'Em Preparo', color: 'bg-orange-500', bgColor: 'bg-orange-50' },
  { id: 'PRONTO', label: 'Pronto (Aguarda Entregador)', color: 'bg-cyan-500', bgColor: 'bg-cyan-50' },
  { id: 'SAIU', label: 'Saiu p/ Entrega', color: 'bg-purple-500', bgColor: 'bg-purple-50' },
  { id: 'ENTREGUE', label: 'Entregues', color: 'bg-emerald-500', bgColor: 'bg-emerald-50' },
];

// ==================== KANBAN COMPONENTS ====================

function KanbanCard({ order, onViewDetails, onAssignDriver }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
    id: order.id,
    data: { order }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / 60000);
    if (diffMinutes < 1) return 'agora';
    if (diffMinutes < 60) return `${diffMinutes}min`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h`;
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}`;
  };

  // Determina o canal de entrega
  // Priorizar external_source para marketplaces
  let channel = order.external_source || order.order_channel || order.delivery_info?.delivery_type || order.origin;
  if (['KEETA', 'IFOOD', '99FOOD'].includes(order.external_source)) {
    channel = order.external_source;
  }
  const channelConfig = CHANNEL_CONFIG[channel] || CHANNEL_CONFIG['PDV'];
  const isPartner = PARTNER_CHANNELS.includes(channel);
  const displayId = order.order_number ? `#${order.order_number}` : `#${order.id.substring(0, 8).toUpperCase()}`;
  
  // Verificar se precisa de entregador
  const needsDriver = order.origin === 'DELIVERY' && 
                      !isPartner && 
                      !order.delivery_info?.driver_id &&
                      (order.status === 'PREPARO' || order.status === 'PRONTO');

  // Cor da borda baseada no canal
  const getBorderColor = () => {
    if (isPartner) return 'border-l-red-500';
    if (order.origin === 'DELIVERY') return 'border-l-blue-500';
    if (order.origin === 'SALAO' || order.origin === 'PDV') return 'border-l-slate-400';
    return 'border-l-emerald-500';
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-sm border border-slate-200 border-l-4 ${getBorderColor()} transition-all select-none ${
        isDragging ? 'shadow-xl scale-[1.02] ring-2 ring-[#FF6B6B]' : 'hover:shadow-md'
      }`}
      data-testid={`kanban-card-${order.id}`}
    >
      {/* Header - Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className="flex items-center justify-between px-3 py-2 cursor-grab active:cursor-grabbing border-b border-slate-100 bg-slate-50/50 rounded-t-lg"
      >
        <span className="font-bold text-[#FF6B6B] text-xs">
          #{displayId}
        </span>
        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${channelConfig.color}`}>
          {channelConfig.label}
        </span>
      </div>
      
      {/* Body */}
      <div className="px-3 py-2 space-y-1.5">
        {/* Cliente */}
        <p className="font-semibold text-slate-800 text-sm truncate leading-tight">
          {order.customer_name || 'Cliente'}
        </p>

        {/* Info Row */}
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <Clock size={10} />
          <span>{formatTime(order.created_at)}</span>
          <span className="text-slate-300">|</span>
          <Package size={10} />
          <span>{order.items?.length || 0}</span>
        </div>

        {/* Endereço (só para delivery) */}
        {order.origin === 'DELIVERY' && order.delivery_info?.address_street && (
          <div className="flex items-start gap-1 text-[10px] text-slate-500 bg-slate-50 px-1.5 py-1 rounded">
            <MapPin size={10} className="mt-0.5 flex-shrink-0 text-slate-400" />
            <span className="truncate">
              {order.delivery_info.address_street}, {order.delivery_info.address_number}{order.delivery_info.address_complement ? ` - ${order.delivery_info.address_complement}` : ''}{order.delivery_info.address_complement ? ` - ${order.delivery_info.address_complement}` : ''}
            </span>
          </div>
        )}

        {/* Entregador atribuído */}
        {order.delivery_info?.driver_name && (
          <div className="flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-1 rounded font-medium">
            <Truck size={10} />
            <span>{order.delivery_info.driver_name}</span>
            {order.delivery_info?.logistics_sync_status === 'FAILED' && (
              <span
                className="ml-auto text-amber-700 bg-amber-100 px-1 rounded"
                title={order.delivery_info.logistics_sync_error || 'iFood Logistics não confirmou atribuição'}
                data-testid="kanban-logistics-warn"
              >⚠ iFood</span>
            )}
          </div>
        )}

        {/* Alerta: precisa de entregador */}
        {needsDriver && (
          <button
            onClick={(e) => { e.stopPropagation(); onAssignDriver(order); }}
            className="w-full flex items-center justify-center gap-1 text-[10px] text-amber-700 bg-amber-100 px-2 py-1.5 rounded font-semibold hover:bg-amber-200 transition-colors"
          >
            <AlertCircle size={10} />
            Atribuir Motoboy
          </button>
        )}
      </div>

      {/* Badge Cancelamento Solicitado Keeta */}
      {order.external_source === 'KEETA' && order.keeta_delivery_info?.cancellation_pending && (
        <div className="mx-3 mb-1 flex items-center gap-1 text-[10px] text-orange-700 bg-orange-100 border border-orange-300 px-2 py-1 rounded font-bold animate-pulse" data-testid={"keeta-cancel-kanban-" + order.id}>
          <AlertCircle size={10} />
          Cancelamento Solicitado
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50/30">
        <span className="text-base font-bold text-slate-900">
          {formatCurrency(order.total)}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onViewDetails(order); }}
          className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title="Ver detalhes"
        >
          <Eye size={12} />
        </button>
      </div>
    </div>
  );
}

function KanbanColumn({ column, orders, onViewDetails, onAssignDriver }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <div
      ref={setNodeRef}
      className={`rounded-xl flex flex-col h-full transition-all min-w-[250px] md:min-w-0 ${
        isOver ? 'ring-2 ring-blue-500 ring-offset-2 bg-blue-100 scale-[1.01]' : 'bg-slate-100/70'
      }`}
      data-testid={`kanban-column-${column.id}`}
      style={{ minHeight: '400px' }}
    >
      {/* Header */}
      <div className={`px-3 py-2.5 border-b border-slate-200/50 flex-shrink-0 ${isOver ? 'bg-blue-200' : ''}`}>
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${column.color}`}></div>
          <h2 className="font-bold text-slate-700 text-xs uppercase tracking-wide">
            {column.label}
          </h2>
          <span className="ml-auto bg-white px-2 py-0.5 rounded-full text-[10px] font-bold text-slate-600 shadow-sm border border-slate-200">
            {orders.length}
          </span>
        </div>
      </div>

      {/* Cards - Área droppable principal */}
      <SortableContext items={orders.map(o => o.id)} strategy={verticalListSortingStrategy}>
        <div className={`space-y-2 overflow-y-auto flex-1 p-2 ${isOver ? 'bg-blue-50/50' : ''}`} style={{ minHeight: '350px' }}>
          {orders.map((order) => (
            <KanbanCard 
              key={order.id} 
              order={order} 
              onViewDetails={onViewDetails}
              onAssignDriver={onAssignDriver}
            />
          ))}
          {orders.length === 0 && (
            <div className={`text-center py-12 ${isOver ? 'text-blue-500' : 'text-slate-400'} text-xs`}>
              <Package size={24} className="mx-auto mb-2 opacity-50" />
              {isOver ? 'Solte aqui!' : 'Vazio'}
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================

// Admin COD Settle Button Component
function AdminCodSettleButton({ orderId, total, onSettled }) {
  const [settling, setSettling] = React.useState(false);
  const [showSettleForm, setShowSettleForm] = React.useState(false);
  const [settleMethod, setSettleMethod] = React.useState('DINHEIRO');
  const [settleAmount, setSettleAmount] = React.useState('');
  const [settleNotes, setSettleNotes] = React.useState('');
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleSettle = async () => {
    if (settling) return;
    setSettling(true);
    try {
      const token = localStorage.getItem('token');
      const amount = settleAmount ? parseFloat(settleAmount) : total;
      const response = await fetch(API_URL + '/api/orders/' + orderId + '/admin-cod-settle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ payment_method: settleMethod, amount, notes: settleNotes || null })
      });
      const data = await response.json();
      if (data.success) {
        if (data.already_settled) {
          alert('Pedido ja estava pago.');
        } else {
          alert('Baixa realizada: R$' + amount.toFixed(2) + ' via ' + settleMethod);
        }
        setShowSettleForm(false);
        if (onSettled) onSettled();
      } else {
        alert('Erro: ' + (data.detail || data.error || 'Erro desconhecido'));
      }
    } catch (err) {
      alert('Erro ao realizar baixa: ' + err.message);
    } finally {
      setSettling(false);
    }
  };

  if (!showSettleForm) {
    return (
      <button
        onClick={() => { setSettleAmount(total ? total.toFixed(2) : ''); setShowSettleForm(true); }}
        className="w-full bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 py-2 px-3 rounded-lg text-sm font-medium transition-colors"
        data-testid="admin-cod-settle-btn"
      >
        Baixa Manual (Admin)
      </button>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2">
      <p className="text-amber-800 text-xs font-semibold">Baixa Manual COD</p>
      <input
        type="number"
        step="0.01"
        value={settleAmount}
        onChange={(e) => setSettleAmount(e.target.value)}
        className="w-full border border-amber-300 rounded px-2 py-1 text-sm"
        placeholder="Valor"
        data-testid="admin-settle-amount"
      />
      <select
        value={settleMethod}
        onChange={(e) => setSettleMethod(e.target.value)}
        className="w-full border border-amber-300 rounded px-2 py-1 text-sm"
        data-testid="admin-settle-method"
      >
        <option value="DINHEIRO">Dinheiro</option>
        <option value="PIX">PIX</option>
        <option value="CARTAO_CREDITO">Cartao Credito</option>
        <option value="CARTAO_DEBITO">Cartao Debito</option>
        <option value="OUTRO">Outro</option>
      </select>
      <input
        type="text"
        value={settleNotes}
        onChange={(e) => setSettleNotes(e.target.value)}
        className="w-full border border-amber-300 rounded px-2 py-1 text-sm"
        placeholder="Obs (opcional)"
        data-testid="admin-settle-notes"
      />
      <div className="flex gap-2">
        <button
          onClick={() => setShowSettleForm(false)}
          className="flex-1 bg-slate-200 text-slate-700 py-1.5 rounded text-sm"
        >
          Cancelar
        </button>
        <button
          onClick={handleSettle}
          disabled={settling || !settleAmount}
          className="flex-1 bg-amber-500 hover:bg-amber-600 text-white py-1.5 rounded text-sm font-medium disabled:opacity-50"
          data-testid="admin-settle-confirm"
        >
          {settling ? 'Processando...' : 'Confirmar Baixa'}
        </button>
      </div>
    </div>
  );
}

export function OrdersPage() {
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    // Iniciar no Kanban se vier com ?view=kanban
    return searchParams.get('view') === 'kanban' ? 'kanban' : 'list';
  });
  const [filter, setFilter] = useState('all');
  const [originFilter, setOriginFilter] = useState('all');
  const [driversList, setDriversList] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [assignDriverModalOpen, setAssignDriverModalOpen] = useState(false);
  const [orderDetailsModalOpen, setOrderDetailsModalOpen] = useState(false);
  
  // === EDICAO DE ITENS ===
  const [editMode, setEditMode] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [addItemSearch, setAddItemSearch] = useState('');
  const [productsList, setProductsList] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [commissionRate99Food, setCommissionRate99Food] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelOrderData, setCancelOrderData] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [registerAsLoss, setRegisterAsLoss] = useState(false);
  
  // === COD PAYMENT VALIDATION (Fase 2) ===
  const [codModalOpen, setCodModalOpen] = useState(false);
  const [codModalOrder, setCodModalOrder] = useState(null);
  const [codPaymentMethod, setCodPaymentMethod] = useState('DINHEIRO');
  const [codAmount, setCodAmount] = useState('');
  const [codOverrideMode, setCodOverrideMode] = useState(false);
  const [codOverrideReason, setCodOverrideReason] = useState('');
  const [codProcessing, setCodProcessing] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelActionLoading, setCancelActionLoading] = useState(false);
  
  // iFood cancellation specific states
  const [ifoodCancelReasons, setIfoodCancelReasons] = useState([]);
  const [selectedIfoodReason, setSelectedIfoodReason] = useState(null);
  const [ifoodLoadingReasons, setIfoodLoadingReasons] = useState(false);
  const [ifoodCanCancel, setIfoodCanCancel] = useState(true);
  const [ifoodCancelMessage, setIfoodCancelMessage] = useState('');
  
  // Keeta cancellation specific states
  const [keetaCancelReasons] = useState([
    { code: 'SYSTEMIC_ISSUES', description: 'Problemas sistêmicos' },
    { code: 'DUPLICATE_APPLICATION', description: 'Pedido duplicado' },
    { code: 'UNAVAILABLE_ITEM', description: 'Item indisponível' },
    { code: 'RESTAURANT_WITHOUT_DELIVERY_PERSON', description: 'Restaurante sem entregador' },
    { code: 'OUTDATED_MENU', description: 'Cardápio desatualizado' },
    { code: 'ORDER_OUTSIDE_THE_DELIVERY_AREA', description: 'Pedido fora da área de entrega' },
    { code: 'BLOCKED_CUSTOMER', description: 'Cliente bloqueado' },
    { code: 'OUTSIDE_DELIVERY_HOURS', description: 'Fora do horário de entrega' },
    { code: 'INTERNAL_DIFFICULTIES_OF_THE_RESTAURANT', description: 'Dificuldades internas do restaurante' },
    { code: 'RISK_AREA', description: 'Área de risco' },
    { code: 'DELIVERY_PROBLEM', description: 'Problema na entrega' },
  ]);
  const [selectedKeetaReason, setSelectedKeetaReason] = useState(null);
  const [activeId, setActiveId] = useState(null);
  
  // Hook de notificação sonora
  const { isEnabled: soundEnabled, setIsEnabled: setSoundEnabled, checkNewOrders, reset: resetNotification } = useNewOrderNotification();

  // Sensores para drag-and-drop (melhorados)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadDrivers = useCallback(async () => {
    try {
      const response = await drivers.list({ available_only: false });
      setDriversList(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar entregadores:', error);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') {
        params.status = filter;
      }
      if (originFilter !== 'all') {
        params.origin = originFilter;
      }
      const response = await ordersApi.list(params);
      
      // Verificar novos pedidos
      if (filter === 'all' && originFilter === 'all') {
        const newOrders = checkNewOrders(response.data);
        if (newOrders.length > 0) {
          newOrders.forEach(order => {
            const orderNum = order.order_number ? `#${order.order_number}` : `#${order.id.substring(0, 8).toUpperCase()}`;
            toast.success(`Novo pedido ${orderNum}`, {
              description: `${order.customer_name || 'Cliente'} - ${formatCurrency(order.total)}`
            });
          });
        }
      }
      
      setOrders(response.data);
    } catch (error) {
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  }, [filter, originFilter, checkNewOrders]);

  
  // === AJUSTES FINANCEIROS ===
  const loadAdjustments = async (orderId) => {
    try {
      const res = await api.get('/orders/' + orderId + '/adjustments');
      setAdjustments(res.data || []);
    } catch (e) {
      setAdjustments([]);
    }
  };

  const resolveAdjustment = async (adjId, newStatus) => {
    try {
      await api.patch('/financial-adjustments/' + adjId, { status: newStatus });
      toast.success(newStatus === 'RESOLVED' ? 'Ajuste marcado como resolvido!' : 'Ajuste ignorado.');
      if (selectedOrder) loadAdjustments(selectedOrder.id);
    } catch (e) {
      toast.error('Erro ao atualizar ajuste');
    }
  };

  // === FUNCOES DE EDICAO ===
  const canEdit = (order) => {
    if (!order) return false;
    if (['ENTREGUE', 'CANCELADO'].includes(order.status)) return false;
    if (order.external_source) return false;
    if (order.payment_mode === 'ONLINE' && ['PAGO', 'PAID', 'APPROVED'].includes(order.payment_status)) return false;
    return true;
  };

  const startEdit = () => {
    if (!selectedOrder) return;
    setEditItems(selectedOrder.items.map(item => ({
      id: item.id,
      product_id: item.product_id,
      product_name: item.product_name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      total_price: item.total_price,
      stock_deducted: item.stock_deducted,
      modifiers: item.modifiers || [],
      notes: item.notes
    })));
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditItems([]);
  };

  const updateItemQty = (idx, delta) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const newQty = Math.max(0, item.quantity + delta);
      return { ...item, quantity: newQty, total_price: item.unit_price * newQty };
    }));
  };

  const removeItem = (idx) => {
    setEditItems(prev => prev.map((item, i) => i === idx ? { ...item, quantity: 0 } : item));
  };

  const editSubtotal = editItems.reduce((sum, item) => sum + (item.quantity > 0 ? item.total_price : 0), 0);
  const editTotal = editSubtotal + (selectedOrder?.delivery_fee || 0) - (selectedOrder?.discount || 0);

  const loadProducts = async () => {
    setProductsLoading(true);
    try {
      const res = await api.get('/products');
      setProductsList(res.data.filter(p => p.is_active));
    } catch (e) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setProductsLoading(false);
    }
  };

  const openAddItem = () => {
    loadProducts();
    setAddItemSearch('');
    setAddItemModalOpen(true);
  };

  const addProduct = (product) => {
    const exists = editItems.findIndex(i => i.product_id === product.id && i.quantity > 0);
    if (exists >= 0) {
      updateItemQty(exists, 1);
    } else {
      setEditItems(prev => [...prev, {
        id: null,
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.price,
        total_price: product.price,
        stock_deducted: false,
        modifiers: [],
        notes: null,
        isNew: true
      }]);
    }
    setAddItemModalOpen(false);
  };

  const saveEdit = async () => {
    if (!selectedOrder) return;
    setEditSaving(true);
    try {
      // Montar mapa de quantidades originais para detectar mudancas
      const originalQtyMap = {};
      selectedOrder.items.forEach(i => { originalQtyMap[i.id] = i.quantity; });
      
      const itemsPayload = editItems
        .filter(item => {
          if (item.isNew) return item.quantity > 0; // Novo item: enviar se qty > 0
          // Item existente: enviar APENAS se qty mudou
          return item.quantity !== originalQtyMap[item.id];
        })
        .map(item => {
          if (item.id) {
            return { id: item.id, quantity: item.quantity };
          } else {
            return { product_id: item.product_id, quantity: item.quantity, notes: item.notes };
          }
        });

      const res = await api.patch('/orders/' + selectedOrder.id + '/items', { items: itemsPayload });
      toast.success('Pedido atualizado com sucesso!');
      loadAdjustments(selectedOrder.id);
      setSelectedOrder(res.data);
      setEditMode(false);
      setEditItems([]);
      loadOrders();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao salvar alteracoes';
      toast.error(msg);
    } finally {
      setEditSaving(false);
    }
  };

  useEffect(() => {
    loadOrders();
    loadDrivers();
    const interval = setInterval(loadOrders, 30000);
    return () => {
      clearInterval(interval);
      resetNotification();
    };
  }, [loadOrders, loadDrivers, resetNotification]);


  // ==================== HANDLERS ====================

  const handleUpdateStatus = async (orderId, newStatus, codOverride = null) => {
    // Fase 2: Interceptar ENTREGUE para pedidos COD internos sem pagamento
    if (newStatus === 'ENTREGUE' && !codOverride) {
      const order = orders.find(o => o.id === orderId);
      if (order && order.payment_mode === 'COD' && 
          !['COLLECTED', 'SETTLED'].includes(order.cod_status) &&
          ['PDV', 'CARDAPIO_DIGITAL'].includes(order.origin) &&
          !['IFOOD', '99FOOD', 'KEETA'].includes(order.external_source)) {
        // Abrir modal COD
        setCodModalOrder(order);
        setCodAmount(String(order.total || ''));
        setCodPaymentMethod('DINHEIRO');
        setCodOverrideMode(false);
        setCodOverrideReason('');
        setCodModalOpen(true);
        return;
      }
    }
    
    try {
      const payload = { status: newStatus };
      if (codOverride) {
        payload.cod_override = true;
        payload.cod_override_reason = codOverride;
      }
      await ordersApi.updateStatus(orderId, newStatus, payload);
      toast.success('Status atualizado!');
      loadOrders();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === 'COD_PAYMENT_REQUIRED') {
        // Backend bloqueou — abrir modal COD
        const order = orders.find(o => o.id === orderId);
        if (order) {
          setCodModalOrder(order);
          setCodAmount(String(order.total || ''));
          setCodModalOpen(true);
          return;
        }
      }
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Erro ao atualizar status');
    }
  };

  // COD Modal: Confirmar pagamento e marcar como entregue
  const handleCodConfirmPayment = async () => {
    if (!codModalOrder) return;
    setCodProcessing(true);
    try {
      // 1. Registrar pagamento COD
      await api.post(`/orders/${codModalOrder.id}/admin-cod-settle`, {
        payment_method: codPaymentMethod,
        amount: parseFloat(codAmount) || codModalOrder.total,
        notes: 'Pagamento confirmado na entrega'
      });
      
      // 2. Marcar como entregue
      await ordersApi.updateStatus(codModalOrder.id, 'ENTREGUE');
      
      toast.success(`Pagamento R$ ${parseFloat(codAmount).toFixed(2)} confirmado e pedido entregue!`);
      setCodModalOpen(false);
      setCodModalOrder(null);
      loadOrders();
    } catch (error) {
      toast.error('Erro ao processar pagamento: ' + (error?.response?.data?.detail || error.message));
    } finally {
      setCodProcessing(false);
    }
  };

  // COD Modal: Exceção controlada (entregar sem pagamento)
  const handleCodOverride = async () => {
    if (!codModalOrder || !codOverrideReason.trim()) {
      toast.error('Informe o motivo da exceção');
      return;
    }
    setCodProcessing(true);
    try {
      await handleUpdateStatus(codModalOrder.id, 'ENTREGUE', codOverrideReason.trim());
      setCodModalOpen(false);
      setCodModalOrder(null);
    } catch (error) {
      toast.error('Erro: ' + (error?.response?.data?.detail || error.message));
    } finally {
      setCodProcessing(false);
    }
  };

  // ==================== CANCELAMENTO ====================
  // Buscar taxa de comissao estimada 99Food (APENAS VISUAL)
  useEffect(() => {
    const fetchCommissionRate = async () => {
      try {
        const resp = await api.get('/v4/99food/commission-rate');
        if (resp.data && resp.data.is_configured) {
          setCommissionRate99Food(resp.data.commission_rate);
        }
      } catch (e) {
        // Silencioso - taxa nao configurada nao eh erro
      }
    };
    fetchCommissionRate();
  }, []);

  const openCancelModal = async (order) => {
    setCancelReason("");
    // Se já iniciou preparo, sugerir registrar como perda
    setRegisterAsLoss(order.status === "PREPARO" || order.status === "PRONTO");
    
    // Reset all platform-specific states
    setSelectedIfoodReason(null);
    setIfoodCancelReasons([]);
    setIfoodCanCancel(true);
    setIfoodCancelMessage('');
    setSelectedKeetaReason(null);
    
    // === SEGREGAÇÃO POR PLATAFORMA ===
    
    // KEETA - usar fluxo próprio da Keeta
    if (order.external_source === "KEETA") {
      try {
        const response = await ordersApi.get(order.id);
        const orderData = response.data || response;
        setCancelOrderData(orderData);
      } catch (error) {
        console.error("Erro ao buscar detalhes do pedido Keeta:", error);
        setCancelOrderData(order);
      }
    }
    // 99FOOD - buscar detalhes completos (inclui connection_id)
    else if (order.external_source === "99FOOD") {
      try {
        const response = await ordersApi.get(order.id);
        setCancelOrderData(response.data || response);
      } catch (error) {
        console.error("Erro ao buscar detalhes do pedido:", error);
        setCancelOrderData(order); // Fallback
      }
    }
    // IFOOD - buscar detalhes e carregar motivos
    else if (order.external_source === "IFOOD") {
      try {
        const response = await ordersApi.get(order.id);
        const orderData = response.data || response;
        setCancelOrderData(orderData);
        loadIfoodCancelReasons(orderData);
      } catch (error) {
        console.error("Erro ao buscar detalhes do pedido iFood:", error);
        setCancelOrderData(order);
        loadIfoodCancelReasons(order);
      }
    }
    // FLUXO GENÉRICO (pedidos internos/locais)
    else {
      setCancelOrderData(order);
    }
    
    setCancelModalOpen(true);
  };

  // Handler para ACEITAR cancelamento 99Food
  const handleAcceptCancellation99Food = async (order) => {
    if (!order.food99_delivery_info?.connection_id) {
      toast.error("Dados de conexão 99Food não encontrados");
      return;
    }
    
    setCancelActionLoading(true);
    try {
      const connectionId = order.food99_delivery_info.connection_id;
      const orderId99 = order.food99_delivery_info.order_id || order.external_id;
      
      const response = await api.post(`/v4/99food/connections/${connectionId}/orders/${orderId99}/accept-cancellation`);
      
      if (response.data.success || response.status === 200 || response.status === 202) {
        toast.success("Cancelamento aceito com sucesso");
        setDetailModalOpen(false);
        loadOrders();
      } else {
        toast.error(response.data.message || "Erro ao aceitar cancelamento");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao aceitar cancelamento");
    } finally {
      setCancelActionLoading(false);
    }
  };

  // Handler para NEGAR cancelamento 99Food
  const handleDenyCancellation99Food = async (order) => {
    if (!order.food99_delivery_info?.connection_id) {
      toast.error("Dados de conexão 99Food não encontrados");
      return;
    }
    
    setCancelActionLoading(true);
    try {
      const connectionId = order.food99_delivery_info.connection_id;
      const orderId99 = order.food99_delivery_info.order_id || order.external_id;
      
      const response = await api.post(`/v4/99food/connections/${connectionId}/orders/${orderId99}/deny-cancellation`, {
        reason: "Pedido já em preparo",
        code: "DISH_ALREADY_DONE"
      });
      
      if (response.data.success || response.status === 200 || response.status === 202) {
        toast.success("Cancelamento negado - pedido continua em andamento");
        setDetailModalOpen(false);
        loadOrders();
      } else {
        toast.error(response.data.message || "Erro ao negar cancelamento");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao negar cancelamento");
    } finally {
      setCancelActionLoading(false);
    }
  };

  // Load iFood cancellation reasons when opening modal for iFood orders
  const loadIfoodCancelReasons = async (order) => {
    if (order.external_source !== 'IFOOD' || !order.external_id) return;
    
    setIfoodLoadingReasons(true);
    setIfoodCanCancel(true);
    setIfoodCancelMessage('');
    setSelectedIfoodReason(null);
    setIfoodCancelReasons([]);
    
    try {
      // Get store_id from the order's store or use default
      const storeId = order.store_id;
      const ifoodOrderId = order.external_id;
      
      const response = await api.get(`/ifood/orders/${storeId}/${ifoodOrderId}/cancellation-reasons`);
      const data = response.data;
      
      if (data.cancellable === true) {
        setIfoodCancelReasons(data.reasons || []);
        setIfoodCanCancel(true);
      } else {
        setIfoodCanCancel(false);
        setIfoodCancelMessage(data.message || 'Este pedido não pode ser cancelado no momento');
      }
    } catch (error) {
      console.error('Error loading iFood cancel reasons:', error);
      setIfoodCanCancel(false);
      setIfoodCancelMessage('Erro ao carregar motivos de cancelamento');
    } finally {
      setIfoodLoadingReasons(false);
    }
  };

  const handleCancelOrder = async () => {
    // === SEGREGAÇÃO POR PLATAFORMA NO CANCELAMENTO ===
    
    // IFOOD specific flow
    if (cancelOrderData?.external_source === 'IFOOD' && cancelOrderData?.external_id) {
      if (!selectedIfoodReason) {
        toast.error("Selecione um motivo de cancelamento");
        return;
      }
      
      setCancelLoading(true);
      try {
        const storeId = cancelOrderData.store_id;
        const ifoodOrderId = cancelOrderData.external_id;
        
        await api.post(`/ifood/orders/${storeId}/${ifoodOrderId}/cancel?reason=${selectedIfoodReason.cancelCodeId}`);
        toast.success("Pedido cancelado!");
        setCancelModalOpen(false);
        setCancelOrderData(null);
        setSelectedIfoodReason(null);
        setIfoodCancelReasons([]);
        loadOrders();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Erro ao cancelar pedido no iFood");
      } finally {
        setCancelLoading(false);
      }
      return;
    }
    
    // KEETA specific flow - usa rota própria da Keeta
    if (cancelOrderData?.external_source === 'KEETA' && cancelOrderData?.external_id) {
      if (!selectedKeetaReason) {
        toast.error("Selecione um motivo de cancelamento");
        return;
      }
      
      setCancelLoading(true);
      try {
        const keetaOrderId = cancelOrderData.external_id;
        
        // Chama a rota correta da Keeta: POST /api/keeta/orders/{order_id}/cancel
        const response = await api.post(`/keeta/orders/${keetaOrderId}/cancel`, {
          cancellation_reason: selectedKeetaReason.code
        });
        
        // CORREÇÃO: Verificar success antes de mostrar toast de sucesso
        if (response.data?.success) {
          toast.success("Pedido cancelado na Keeta!");
        } else {
          // Se a API retornou HTTP 200 mas success=false, mostra erro real
          toast.error(response.data?.message || "Erro ao cancelar pedido na Keeta");
          setCancelLoading(false);
          return;
        }
        setCancelModalOpen(false);
        setCancelOrderData(null);
        setSelectedKeetaReason(null);
        loadOrders();
      } catch (error) {
        console.error("Erro ao cancelar pedido Keeta:", error);
        toast.error(error.response?.data?.detail || "Erro ao cancelar pedido na Keeta");
      } finally {
        setCancelLoading(false);
      }
      return;
    }
    
    // 99FOOD and generic flow - validation
    if (!cancelOrderData || !cancelReason.trim()) {
      toast.error("Informe o motivo do cancelamento");
      return;
    }

    setCancelLoading(true);
    try {
      let response;
      
      // Para pedidos 99Food, usar o endpoint específico da integração
      if (cancelOrderData.external_source === "99FOOD" && cancelOrderData.food99_delivery_info?.connection_id) {
        const connectionId = cancelOrderData.food99_delivery_info.connection_id;
        const orderId99 = cancelOrderData.food99_delivery_info.order_id || cancelOrderData.external_id;
        
        // Mapeamento de motivos para códigos 99Food
        // Mapeamento de motivos para códigos oficiais da API 99Food
        const reasonCodeMap99Food = {
          "SYSTEMIC_ISSUES": "SYSTEMIC_ISSUES",
          "DUPLICATE_APPLICATION": "DUPLICATE_APPLICATION",
          "UNAVAILABLE_ITEM": "UNAVAILABLE_ITEM",
          "RESTAURANT_WITHOUT_DELIVERY_PERSON": "RESTAURANT_WITHOUT_DELIVERY_PERSON",
          "OUTDATED_MENU": "OUTDATED_MENU",
          "ORDER_OUTSIDE_THE_DELIVERY_AREA": "ORDER_OUTSIDE_THE_DELIVERY_AREA",
          "BLOCKED_CUSTOMER": "BLOCKED_CUSTOMER",
          "OUTSIDE_DELIVERY_HOURS": "OUTSIDE_DELIVERY_HOURS",
          "INTERNAL_DIFFICULTIES_OF_THE_RESTAURANT": "INTERNAL_DIFFICULTIES_OF_THE_RESTAURANT",
          "RISK_AREA": "RISK_AREA",
          "DELIVERY_PROBLEM": "DELIVERY_PROBLEM"
        };
        
        // Labels amigáveis para exibição
        const reasonLabels99Food = {
          "SYSTEMIC_ISSUES": "Problemas sistêmicos",
          "DUPLICATE_APPLICATION": "Pedido duplicado",
          "UNAVAILABLE_ITEM": "Item indisponível",
          "RESTAURANT_WITHOUT_DELIVERY_PERSON": "Restaurante sem entregador",
          "OUTDATED_MENU": "Cardápio desatualizado",
          "ORDER_OUTSIDE_THE_DELIVERY_AREA": "Pedido fora da área de entrega",
          "BLOCKED_CUSTOMER": "Cliente bloqueado",
          "OUTSIDE_DELIVERY_HOURS": "Fora do horário de entrega",
          "INTERNAL_DIFFICULTIES_OF_THE_RESTAURANT": "Dificuldades internas do restaurante",
          "RISK_AREA": "Área de risco",
          "DELIVERY_PROBLEM": "Problema na entrega"
        };
        
        const code = reasonCodeMap99Food[cancelReason] || "SYSTEMIC_ISSUES";
        const reasonLabel = reasonLabels99Food[cancelReason] || cancelReason;
        
        response = await api.post(`/v4/99food/connections/${connectionId}/orders/${orderId99}/cancel`, {
          reason: reasonLabel,
          code: code,
          mode: "MANUAL"
        });
        
        if (response.data.success || response.status === 200 || response.status === 202) {
          toast.success("Solicitação de cancelamento enviada para 99Food");
          setCancelModalOpen(false);
          setCancelOrderData(null);
          loadOrders();
        } else {
          toast.error(response.data.message || "Erro ao cancelar no 99Food");
        }
      } else if (cancelOrderData.origin === "CARDAPIO_DIGITAL" || cancelOrderData.order_channel === "CARDAPIO_DIGITAL") {
        // CARDÁPIO DIGITAL - endpoint exclusivo isolado (NÃO usa o genérico)
        response = await api.post("/pdv/orders/" + cancelOrderData.id + "/cancel-digital", {
          reason: cancelReason
        });
        if (response.data.success) {
          const msg = response.data.registered_as_loss 
            ? "Pedido cancelado e registrado como perda operacional"
            : "Pedido do Cardápio Digital cancelado com sucesso";
          toast.success(msg);
          setCancelModalOpen(false);
          setCancelOrderData(null);
          loadOrders();
        } else {
          toast.error(response.data.message || "Erro ao cancelar");
        }
      } else {        // Para outros pedidos, usar endpoint padrão do PDV
        response = await api.post("/pdv/orders/" + cancelOrderData.id + "/cancel", {
          reason: cancelReason
        });

        if (response.data.success) {
          const msg = response.data.registered_as_loss 
            ? "Pedido cancelado e registrado como perda operacional"
            : "Pedido cancelado com sucesso";
          toast.success(msg);
          setCancelModalOpen(false);
          setCancelOrderData(null);
          loadOrders();
        } else {
          toast.error(response.data.message || "Erro ao cancelar");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cancelar pedido");
    } finally {
      setCancelLoading(false);
    }
  };


  const isOwnDelivery = (order) => {
    const channel = order.external_source || order.order_channel;
    const deliveryType = order.delivery_info?.delivery_type;
    return order.origin === 'DELIVERY' && 
           !PARTNER_CHANNELS.includes(channel) && 
           (!deliveryType || deliveryType === 'LOJA');
  };

  const isPartnerDelivery = (order) => {
    const channel = order.external_source || order.order_channel;
    const deliveryType = order.delivery_info?.delivery_type;
    return PARTNER_CHANNELS.includes(channel) || PARTNER_CHANNELS.includes(deliveryType);
  };

  const openAssignDriverModal = (order) => {
    setSelectedOrder(order);
    setAssignDriverModalOpen(true);
  };

  const handleAssignDriver = async (driverId) => {
    if (!selectedOrder || !selectedOrder.delivery_info) {
      toast.error('Informação de entrega não encontrada');
      return;
    }

    try {
      await delivery.assignDriver(selectedOrder.delivery_info.id, driverId);
      toast.success('Entregador atribuído com sucesso!');
      setAssignDriverModalOpen(false);
      setSelectedOrder(null);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atribuir entregador');
    }
  };

  const handleDispatch = async (order) => {
    if (isOwnDelivery(order) && !order.delivery_info?.driver_id) {
      toast.error('Atribua um entregador antes de despachar');
      openAssignDriverModal(order);
      return;
    }

    try {
      await ordersApi.dispatch(order.id);
      toast.success('Pedido despachado!');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao despachar pedido');
    }
  };

  const openOrderDetails = async (order) => {
    // Buscar detalhes completos do pedido para ter discount_details
    try {
      const response = await ordersApi.get(order.id);
      setSelectedOrder(response.data || response);
    } catch (error) {
      console.error('Erro ao buscar detalhes do pedido:', error);
      setSelectedOrder(order); // Fallback para dados da listagem
    }
    setOrderDetailsModalOpen(true);
    loadAdjustments(order.id);
    setEditMode(false);
  };

  // Imprimir pedido via orquestrador unificado
  const handlePrintOrder = async (order) => {
    if (!thermalPrinter.isConnected) {
      toast.error('Impressora não conectada. Conecte via Configurações > Impressoras.');
      return;
    }
    try {
      const result = await thermalPrinter.printOrder(order, 'Hit Grill');
      const orderNum = order.order_number || order.display_id || order.id?.substring(0, 8).toUpperCase();
      if (result.success) {
        toast.success(`Pedido #${orderNum} impresso com sucesso`, { duration: 3000 });
      } else {
        toast.error('Falha na impressão');
      }
    } catch (error) {
      console.error('Erro ao imprimir:', error);
      toast.error('Erro ao imprimir pedido');
    }
  };

  // ==================== KANBAN DRAG HANDLERS ====================

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const orderId = active.id;
    let newStatus = over.id;

    // Se o over.id não é uma coluna válida, pode ser um card dentro da coluna
    // Nesse caso, precisamos encontrar a coluna pai
    const isValidColumn = KANBAN_COLUMNS.some(col => col.id === newStatus);
    if (!isValidColumn) {
      // Tenta encontrar o pedido alvo e pegar seu status
      const targetOrder = orders.find(o => o.id === over.id);
      if (targetOrder) {
        newStatus = targetOrder.status;
      } else {
        return; // Não conseguiu determinar a coluna
      }
    }

    // Encontrar o pedido
    const order = orders.find(o => o.id === orderId);
    if (!order || order.status === newStatus) return;

    // ✅ OTIMISTA: Atualiza imediatamente na UI
    const previousOrders = [...orders];
    setOrders(prev => prev.map(o => 
      o.id === orderId ? { ...o, status: newStatus } : o
    ));

    // Validações especiais para transição para SAIU
    if (newStatus === 'SAIU') {
      // Se está indo para SAIU e é delivery próprio, precisa ter entregador
      if (isOwnDelivery(order) && !order.delivery_info?.driver_id) {
        toast.error('Atribua um entregador antes de despachar');
        setOrders(previousOrders); // Reverte
        openAssignDriverModal(order);
        return;
      }
      
      // Usar endpoint de dispatch para delivery
      if (order.origin === 'DELIVERY') {
        try {
          await ordersApi.dispatch(orderId);
          toast.success('Pedido despachado!');
          loadOrders();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Erro ao despachar');
          setOrders(previousOrders); // Reverte em caso de erro
        }
        return;
      }
    }

    // Validação para transição de PRONTO para SAIU (delivery próprio precisa de entregador)
    if (order.status === 'PRONTO' && newStatus === 'SAIU' && isOwnDelivery(order) && !order.delivery_info?.driver_id) {
      toast.error('Atribua um entregador antes de despachar');
      setOrders(previousOrders); // Reverte
      openAssignDriverModal(order);
      return;
    }

    // Atualização normal de status
    try {
      await ordersApi.updateStatus(orderId, newStatus);
      toast.success(`Status atualizado para ${getStatusLabel(newStatus)}`);
    } catch (error) {
      toast.error('Erro ao atualizar status');
      setOrders(previousOrders); // Reverte em caso de erro
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const getOrdersByStatus = (status) => {
    return orders.filter(order => order.status === status);
  };

  const activeOrder = activeId ? orders.find(order => order.id === activeId) : null;

  // ==================== RENDER HELPERS ====================

  const getChannelDisplay = (order) => {
    // Priorizar external_source para pedidos de marketplace
    let channel = order.external_source || order.order_channel || order.delivery_info?.delivery_type || order.origin;
    // Para Keeta/iFood/99Food, usar o source diretamente
    if (['KEETA', 'IFOOD', '99FOOD'].includes(order.external_source)) {
      channel = order.external_source;
    }
    if (order.origin === 'DELIVERY' && !PARTNER_CHANNELS.includes(channel)) {
      channel = 'LOJA';
    }
    const config = CHANNEL_CONFIG[channel] || CHANNEL_CONFIG['PDV'];
    const isPartner = PARTNER_CHANNELS.includes(channel);
    const Icon = config.icon;
    
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 w-fit ${config.color}`}>
        <Icon size={12} />
        {config.label}
        {isPartner && <span className="ml-0.5">🔒</span>}
      </span>
    );
  };

  const statusFilters = [
    { value: 'all', label: 'Todos' },
    { value: 'PENDENTE', label: 'Pendentes' },
    { value: 'CONFIRMADO', label: 'Confirmados' },
    { value: 'PREPARO', label: 'Em Preparo' },
    { value: 'PRONTO', label: 'Pronto' },
    { value: 'SAIU', label: 'Saiu' },
    { value: 'ENTREGUE', label: 'Entregues' },
  ];

  const originFilters = [
    { value: 'all', label: 'Todas' },
    { value: 'PDV', label: 'Balcão' },
    { value: 'DELIVERY', label: 'Delivery' },
    { value: 'COMANDA', label: 'Comanda' },
    { value: 'CARDAPIO_DIGITAL', label: 'QRpedir' },
  ];

  const renderActions = (order) => {
    const isDelivery = order.origin === 'DELIVERY';
    const ownDelivery = isOwnDelivery(order);
    const partnerDelivery = isPartnerDelivery(order);
    const hasDriver = order.delivery_info?.driver_id;
    const driverName = order.delivery_info?.driver_name;

    return (
      <div className="flex gap-2 flex-wrap items-center">
        <Button size="sm" variant="ghost" onClick={() => handlePrintOrder(order)} title="Imprimir pedido" disabled={!thermalPrinter.isConnected} data-testid={"list-print-btn-" + order.id}>
                    <Printer size={16} className={!thermalPrinter.isConnected ? 'opacity-30' : ''} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openOrderDetails(order)} title="Ver detalhes">
          <Eye size={16} />
        </Button>

        {order.status !== "ENTREGUE" && order.status !== "CANCELADO" && (
          <Button size="sm" variant="ghost" onClick={() => openCancelModal(order)} title="Cancelar pedido" className="text-red-500 hover:text-red-700 hover:bg-red-50">
            <XCircle size={16} />
          </Button>
        )}

        {order.status === 'PENDENTE' && (
          <Button size="sm" onClick={() => handleUpdateStatus(order.id, 'CONFIRMADO')} className="bg-emerald-600 hover:bg-emerald-700">
            <CheckCircle size={14} className="mr-1" />
            Confirmar
          </Button>
        )}

        {order.status === 'CONFIRMADO' && (
          <Button size="sm" onClick={() => handleUpdateStatus(order.id, 'PREPARO')} className="bg-orange-500 hover:bg-orange-600">
            Iniciar Preparo
          </Button>
        )}

        {order.status === 'PREPARO' && (
          <>
            {isDelivery && ownDelivery && !hasDriver && (
              <Button size="sm" variant="outline" onClick={() => openAssignDriverModal(order)} className="border-blue-500 text-blue-600 hover:bg-blue-50">
                <UserPlus size={14} className="mr-1" />
                Atribuir Motoboy
              </Button>
            )}

            {isDelivery && ownDelivery && hasDriver && driverName && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1">
                <Truck size={12} />
                {driverName}
              </span>
            )}

            {isDelivery ? (
              <Button size="sm" onClick={() => handleUpdateStatus(order.id, 'PRONTO')} className="bg-cyan-600 hover:bg-cyan-700">
                <CheckCircle size={14} className="mr-1" />
                Marcar Pronto
              </Button>
            ) : (
              <Button size="sm" onClick={() => handleUpdateStatus(order.id, 'ENTREGUE')} className="bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle size={14} className="mr-1" />
                Finalizar
              </Button>
            )}
          </>
        )}

        {order.status === 'PRONTO' && (
          <>
            {isDelivery && ownDelivery && !hasDriver && (
              <Button size="sm" variant="outline" onClick={() => openAssignDriverModal(order)} className="border-amber-500 text-amber-600 hover:bg-amber-50">
                <UserPlus size={14} className="mr-1" />
                Atribuir Motoboy
              </Button>
            )}

            {isDelivery && ownDelivery && hasDriver && driverName && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1">
                <Truck size={12} />
                {driverName}
              </span>
            )}

            {isDelivery ? (
              <Button size="sm" onClick={() => handleDispatch(order)} className="bg-blue-600 hover:bg-blue-700" disabled={ownDelivery && !hasDriver}>
                <Send size={14} className="mr-1" />
                {partnerDelivery ? 'Pronto p/ Retirada' : 'Despachar'}
              </Button>
            ) : (
              <Button size="sm" onClick={() => handleUpdateStatus(order.id, 'ENTREGUE')} className="bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle size={14} className="mr-1" />
                Finalizar
              </Button>
            )}
          </>
        )}

        {order.status === 'SAIU' && (
          <Button size="sm" onClick={() => handleUpdateStatus(order.id, 'ENTREGUE')} className="bg-emerald-600 hover:bg-emerald-700">
            <CheckCircle size={14} className="mr-1" />
            Entregue
          </Button>
        )}
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="orders-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              Pedidos
            </h1>
            <p className="text-slate-600">
              {viewMode === 'list' ? 'Visualização em lista' : 'Arraste os cards para mudar o status'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Toggle de visualização */}
            <div className="flex bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="view-mode-list"
              >
                <List size={16} />
                Lista
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'kanban' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="view-mode-kanban"
              >
                <LayoutGrid size={16} />
                Kanban
              </button>
            </div>

            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                soundEnabled ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
              data-testid="toggle-sound-btn"
            >
              {soundEnabled ? <Bell size={20} /> : <BellOff size={20} />}
              <span className="text-sm font-medium hidden sm:inline">
                {soundEnabled ? 'Som Ativo' : 'Som Off'}
              </span>
            </button>

            <Button onClick={loadOrders} className="bg-[#FF6B6B] hover:bg-[#FF7A5A]" data-testid="refresh-orders-btn">
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>


        {/* Filtros */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between bg-white p-4 rounded-xl shadow-sm border border-slate-100">
          <div>
            <p className="text-xs text-slate-500 uppercase font-semibold mb-2">Status</p>
            <div className="flex gap-2 flex-wrap">
              {statusFilters.map((sf) => (
                <button
                  key={sf.value}
                  onClick={() => setFilter(sf.value)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    filter === sf.value ? 'bg-[#FF6B6B] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {sf.label}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <p className="text-xs text-slate-500 uppercase font-semibold mb-2">Origem</p>
            <div className="flex gap-2 flex-wrap">
              {originFilters.map((of) => (
                <button
                  key={of.value}
                  onClick={() => setOriginFilter(of.value)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    originFilter === of.value ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {of.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Conteúdo baseado no viewMode */}
        {viewMode === 'list' ? (
          /* ==================== LISTA ==================== */
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            {/* Cards mobile (< lg) */}
            <div className="lg:hidden divide-y divide-slate-100">
              {loading ? (
                <div className="px-4 py-10 text-center text-slate-500">
                  <RefreshCw size={22} className="animate-spin mx-auto mb-2" />
                  Carregando...
                </div>
              ) : orders.length > 0 ? (
                orders.map((order) => (
                  <div
                    key={`m-${order.id}`}
                    data-testid={`order-card-mobile-${order.id}`}
                    className="px-4 py-3 active:bg-slate-50"
                    onClick={() => { setSelectedOrder(order); setShowDetail(true); }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded">
                            {order.order_number ? `#${order.order_number}` : `#${order.id.substring(0, 8).toUpperCase()}`}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getStatusColor(order.status)}`}>
                            {getStatusLabel(order.status)}
                          </span>
                        </div>
                        <p className="text-sm font-medium mt-1 truncate">
                          {order.customer_name || 'Anônimo'}
                        </p>
                        {order.delivery_info?.address_street && (
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5 truncate">
                            <MapPin size={10} className="shrink-0" />
                            <span className="truncate">
                              {order.delivery_info.address_street}, {order.delivery_info.address_number}
                            </span>
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <span>{order.origin}</span>
                          <span>·</span>
                          <span>{order.items?.length || 0} item(s)</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-slate-900">{formatCurrency(order.total)}</p>
                        <div className="mt-1 flex justify-end">{renderActions(order)}</div>
                      </div>
                    </div>
                    {order.external_source === 'KEETA' && order.keeta_delivery_info?.cancellation_pending && (
                      <div className="mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-300 inline-block animate-pulse">
                        Cancelamento Solicitado
                      </div>
                    )}
                    {order.external_source === 'IFOOD' && order.ifood_details?.sync?.status && order.ifood_details.sync.status !== 'OK' && (
                      <div className="mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-300 inline-block">
                        iFood desincronizado
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="px-4 py-10 text-center">
                  <Package size={40} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500 text-sm">Nenhum pedido encontrado</p>
                </div>
              )}
            </div>

            {/* Tabela desktop (>= lg) */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Pedido</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Cliente</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Origem</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Itens</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Valor</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-8 text-center text-slate-500">
                        <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                        Carregando...
                      </td>
                    </tr>
                  ) : orders.length > 0 ? (
                    orders.map((order) => (
                      <tr key={order.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <span className="text-sm font-bold text-slate-900 bg-slate-100 px-2 py-1 rounded">
                            {order.order_number ? `#${order.order_number}` : `#${order.id.substring(0, 8).toUpperCase()}`}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium">{order.customer_name || 'Anônimo'}</p>
                          {order.delivery_info?.address_street && (
                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <MapPin size={10} />
                              {order.delivery_info.address_street}, {order.delivery_info.address_number}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-slate-500">{order.origin}</span>
                            {getChannelDisplay(order)}
                            {order.delivery_info?.driver_name && (
                              <span className="text-xs text-blue-600 flex items-center gap-1">
                                <Truck size={10} />
                                {order.delivery_info.driver_name}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{order.items?.length || 0}</td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                              {getStatusLabel(order.status)}
                            </span>
                            {order.external_source === 'KEETA' && order.keeta_delivery_info?.cancellation_pending && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-300 animate-pulse" data-testid={"keeta-cancel-badge-" + order.id}>
                                Cancelamento Solicitado
                              </span>
                            )}
                            {order.external_source === 'IFOOD' && order.ifood_details?.sync?.status && order.ifood_details.sync.status !== 'OK' && (
                              <span
                                className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-300"
                                data-testid={"ifood-sync-badge-" + order.id}
                                title={order.ifood_details.sync.error || 'iFood fora de sincronia'}
                              >
                                iFood desincronizado
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold">{formatCurrency(order.total)}</td>
                        <td className="px-6 py-4">{renderActions(order)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <Package size={48} className="mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500">Nenhum pedido encontrado</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* /tabela desktop */}
          </div>
        ) : (
          /* ==================== KANBAN ==================== */
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div className="flex md:grid md:grid-cols-6 gap-4 h-[calc(100vh-320px)] min-h-[500px] overflow-x-auto pb-4" style={{ minWidth: 0 }}>
              {KANBAN_COLUMNS.map((column) => (
                <KanbanColumn
                  key={column.id}
                  column={column}
                  orders={getOrdersByStatus(column.id)}
                  onViewDetails={openOrderDetails}
                  onAssignDriver={openAssignDriverModal}
                />
              ))}
            </div>

            <DragOverlay dropAnimation={null}>
              {activeOrder ? (
                <div className="bg-white rounded-lg p-3 shadow-2xl border-2 border-[#FF6B6B] w-[280px] rotate-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-[#FF6B6B] text-sm bg-red-50 px-2 py-0.5 rounded">
                      #{activeOrder.display_id || activeOrder.id.substring(0, 8).toUpperCase()}
                    </span>
                  </div>
                  <h3 className="font-medium text-slate-900 text-sm">{activeOrder.customer_name || 'Cliente'}</h3>
                  <p className="text-lg font-bold text-slate-900 mt-2">{formatCurrency(activeOrder.total)}</p>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Modal de Atribuir Entregador */}
      <Dialog open={assignDriverModalOpen} onOpenChange={setAssignDriverModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={20} />
              Atribuir Entregador
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedOrder && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p className="font-medium">Pedido #{selectedOrder.display_id || selectedOrder.id?.substring(0, 8).toUpperCase()}</p>
                <p className="text-slate-600">{selectedOrder.customer_name || 'Cliente'} - {formatCurrency(selectedOrder.total)}</p>
                {selectedOrder.delivery_info?.address_street && (
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <MapPin size={12} />
                    {selectedOrder.delivery_info.address_street}, {selectedOrder.delivery_info.address_number}{selectedOrder.delivery_info.address_complement ? ` - ${selectedOrder.delivery_info.address_complement}` : ''}
                  </p>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">Selecione um entregador:</p>
              {driversList.filter(d => d.is_active !== false).length > 0 ? (
                <div className="grid gap-2 max-h-60 overflow-y-auto">
                  {driversList.filter(d => d.is_active !== false).map((driver) => (
                    <button
                      key={driver.id}
                      onClick={() => handleAssignDriver(driver.id)}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{driver.name}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Phone size={10} />
                          {driver.phone || 'Sem telefone'}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${driver.is_available ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {driver.is_available ? 'Disponível' : 'Em entrega'}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <AlertCircle size={32} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500 text-sm">Nenhum entregador cadastrado</p>
                  <p className="text-xs text-slate-400 mt-1">Cadastre motoboys em Delivery → Motoboys</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes do Pedido */}
      <Dialog open={orderDetailsModalOpen} onOpenChange={setOrderDetailsModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between w-full pr-8">
              <span>Pedido #{selectedOrder?.display_id || selectedOrder?.id?.substring(0, 8).toUpperCase()}</span>
              <button
                onClick={() => handlePrintOrder(selectedOrder)}
                disabled={!thermalPrinter.isConnected}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                  thermalPrinter.isConnected 
                    ? 'bg-slate-800 text-white hover:bg-slate-700' 
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
                title={thermalPrinter.isConnected ? 'Imprimir pedido' : 'Impressora não conectada'}
                data-testid="order-detail-print-btn"
              >
                <Printer size={16} />
                Imprimir
              </button>
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4 py-4">
              {/* Alerta de Cancelamento Solicitado Keeta */}
              {selectedOrder.external_source === 'KEETA' && selectedOrder.keeta_delivery_info?.cancellation_pending && (
                <div className="flex items-center gap-2 px-4 py-3 bg-orange-50 border border-orange-300 rounded-lg" data-testid="keeta-cancel-modal-alert">
                  <AlertCircle size={18} className="text-orange-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-orange-800">Cancelamento Solicitado</p>
                    <p className="text-xs text-orange-600">
                      {selectedOrder.keeta_delivery_info?.cancellation_request_info?.reason || 'Motivo não informado'}
                      {selectedOrder.keeta_delivery_info?.cancellation_request_info?.cancelled_by && (
                        <span> — Solicitado por: {selectedOrder.keeta_delivery_info.cancellation_request_info.cancelled_by}</span>
                      )}
                    </p>
                  </div>
                </div>
              )}
              {/* Info 99Food MERCHANT - Entrega Propria */}
              {selectedOrder.external_source === '99FOOD' && selectedOrder.food99_delivery_info && !selectedOrder.food99_delivery_info.is_marketplace && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200" data-testid="99food-merchant-info">
                  <p className="text-xs text-yellow-700 uppercase font-semibold mb-2 flex items-center gap-1">
                    <Truck size={14} />
                    99Food - Entrega Propria (MERCHANT)
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {selectedOrder.food99_delivery_info.locator && (
                      <div>
                        <p className="text-yellow-600 text-xs">Localizador</p>
                        <p className="font-bold text-lg">{selectedOrder.food99_delivery_info.locator}</p>
                      </div>
                    )}
                    {selectedOrder.food99_delivery_info.delivery_code && (
                      <div>
                        <p className="text-yellow-600 text-xs">Codigo de Entrega (motoboy)</p>
                        <p className="font-bold text-lg font-mono">{selectedOrder.food99_delivery_info.delivery_code}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-yellow-600 text-xs">Confirmacao via App Motoboy</p>
                      <p className={selectedOrder.food99_delivery_info.delivery_code_required ? 'font-bold text-orange-600' : 'font-bold text-green-600'}>
                        {selectedOrder.food99_delivery_info.delivery_code_required ? 'Obrigatorio no app' : 'Nao exigido'}
                      </p>
                    </div>
                    <div>
                      <p className="text-yellow-600 text-xs">Status Validacao (motoboy)</p>
                      <p className={selectedOrder.food99_delivery_info.delivery_code_validated_at ? 'font-bold text-green-600' : 'font-bold text-slate-400'}>
                        {selectedOrder.food99_delivery_info.delivery_code_validated_at 
                          ? 'Validado em ' + new Date(selectedOrder.food99_delivery_info.delivery_code_validated_at).toLocaleString('pt-BR')
                          : selectedOrder.food99_delivery_info.delivery_code_validation_error || 'Aguardando motoboy'
                        }
                      </p>
                    </div>
                  </div>
                  {/* P1: Badge de confirmacao de entrega */}
                  {selectedOrder.food99_delivery_info.delivery_confirmation_type && (() => {
                    const t = selectedOrder.food99_delivery_info.delivery_confirmation_type;
                    const map = {
                      MOTOBOY_CODE_VALIDATED:           { color: 'bg-green-100 text-green-800 border-green-300',  icon: '✓', title: 'Validado pelo Motoboy', desc: 'Codigo confirmado via app do entregador.' },
                      '99FOOD_CUSTOMER_CONFIRMED':      { color: 'bg-blue-100 text-blue-800 border-blue-300',     icon: '✓', title: 'Confirmado pelo Cliente', desc: 'Pedido confirmado via aplicativo 99Food.' },
                      VALIDATION_FAILED_BUT_CONCLUDED:  { color: 'bg-red-100 text-red-800 border-red-300',        icon: '⚠', title: 'Codigo rejeitado', desc: 'Motoboy informou codigo invalido. Entrega posteriormente concluida via 99Food.' },
                      ADMIN_MANUAL:                     { color: 'bg-amber-100 text-amber-800 border-amber-300',  icon: '⚠', title: 'Confirmado sem validacao do motoboy', desc: '99Food confirmou entrega sem validacao local (finalizacao manual).' },
                      UNKNOWN:                          { color: 'bg-slate-100 text-slate-700 border-slate-300',  icon: '?', title: 'Origem desconhecida', desc: 'Nao foi possivel classificar como a entrega foi confirmada.' },
                    };
                    const b = map[t] || map.UNKNOWN;
                    return (
                      <div className={`mt-3 px-3 py-2 rounded-md border ${b.color}`} data-testid="delivery-confirmation-badge">
                        <div className="text-sm font-semibold flex items-center gap-2">
                          <span>{b.icon}</span>
                          <span>{b.title}</span>
                        </div>
                        <div className="text-xs mt-1 opacity-80">{b.desc}</div>
                      </div>
                    );
                  })()}
                </div>
              )}

                            <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Cliente</p>
                  <p className="font-medium">{selectedOrder.customer_name || 'Anônimo'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Status</p>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(selectedOrder.status)}`}>
                    {getStatusLabel(selectedOrder.status)}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Origem</p>
                  <p>{selectedOrder.origin}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Canal</p>
                  {getChannelDisplay(selectedOrder)}
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Data</p>
                  <p className="flex items-center gap-1 text-sm">
                    <Clock size={14} />
                    {formatDate(selectedOrder.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Total</p>
                  <p className="text-lg font-bold text-emerald-600">{formatCurrency(selectedOrder.total)}</p>
                </div>
              </div>

              {/* Informações de Pagamento - Cardápio Digital COD */}
              {selectedOrder.payment_mode === "COD" && selectedOrder.origin === "CARDAPIO_DIGITAL" && (
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200" data-testid="cod-payment-info">
                  <p className="text-xs text-amber-700 uppercase font-semibold mb-2 flex items-center gap-1">
                    <Banknote size={14} />
                    Pagamento na Entrega
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-amber-600 text-xs">Forma</p>
                      <p className="font-bold">
                        {selectedOrder.cod_payment_type === 'DINHEIRO' ? 'Dinheiro' :
                         selectedOrder.cod_payment_type === 'CARTAO_ENTREGA' ? 'Cartao na Entrega' :
                         selectedOrder.cod_payment_type === 'VEROCARD' ? 'Verocard' :
                         selectedOrder.cod_payment_type === 'SODEXO' ? 'Sodexo' :
                         selectedOrder.cod_payment_type === 'VR' ? 'VR' :
                         selectedOrder.cod_payment_type === 'PLUXE' ? 'Pluxe' :
                         'Nao informado'}
                      </p>
                    </div>
                    <div>
                      <p className="text-amber-600 text-xs">Status COD</p>
                      <p className={selectedOrder.cod_status === 'COLLECTED' ? 'font-bold text-green-600' : 'font-bold text-amber-600'}>
                        {selectedOrder.cod_status === 'COLLECTED' ? 'Recebido' : 
                         selectedOrder.cod_status === 'EXPECTED' ? 'Aguardando' : 
                         selectedOrder.cod_status || 'Pendente'}
                      </p>
                    </div>
                    {selectedOrder.cod_payment_type === 'DINHEIRO' && selectedOrder.change_for && selectedOrder.change_for > 0 && (
                      <div className="col-span-2 bg-white p-2 rounded border border-amber-100">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-amber-600" />
                          <div>
                            <span className="text-amber-600 text-xs">Troco para</span>
                            <p className="font-bold">{formatCurrency(selectedOrder.change_for)}</p>
                            <p className="text-xs text-slate-500">{'Troco: ' + formatCurrency(selectedOrder.change_for - selectedOrder.total)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedOrder.cod_status !== 'COLLECTED' &&
                     selectedOrder.payment_status !== 'PAGO' &&
                     selectedOrder.payment_status !== 'PAID' &&
                     selectedOrder.payment_status !== 'APPROVED' && (
                      <div className="col-span-2 mt-2">
                        <AdminCodSettleButton
                          orderId={selectedOrder.id}
                          total={selectedOrder.total}
                          onSettled={() => loadOrders()}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Motivo do Cancelamento - Com suporte Keeta cancel_info */}
              {selectedOrder.status === "CANCELADO" && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-xs text-red-700 uppercase font-semibold mb-2 flex items-center gap-1">
                    <AlertCircle size={14} />
                    Pedido Cancelado
                  </p>
                  <div className="space-y-2">
                    {/* Badge de origem - Keeta cancel_info ou fallback local */}
                    {(selectedOrder.keeta_delivery_info?.cancel_info?.cancel_source || selectedOrder.cancellation_requested_by) && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          {selectedOrder.keeta_delivery_info?.cancel_info?.cancel_source || selectedOrder.cancellation_requested_by || "Desconhecido"}
                        </span>
                      </div>
                    )}
                    
                    {/* Motivo - prioriza cancel_info.reason_description */}
                    {(selectedOrder.keeta_delivery_info?.cancel_info?.reason_description || selectedOrder.cancellation_reason) && (
                      <p className="text-sm">
                        <span className="font-medium text-red-700">Motivo:</span>{" "}
                        {selectedOrder.keeta_delivery_info?.cancel_info?.reason_description || selectedOrder.cancellation_reason}
                      </p>
                    )}
                    
                    {/* Código técnico */}
                    {(selectedOrder.keeta_delivery_info?.cancel_info?.reason_code || selectedOrder.cancellation_code) && (
                      <p className="text-xs text-red-500">
                        <span className="font-medium">Código:</span>{" "}
                        {selectedOrder.keeta_delivery_info?.cancel_info?.reason_code || selectedOrder.cancellation_code}
                      </p>
                    )}
                    
                    {/* Data/hora do cancelamento */}
                    {(selectedOrder.keeta_delivery_info?.cancel_info?.cancelled_at || selectedOrder.cancelled_at) && (
                      <p className="text-xs text-red-500">
                        Cancelado em: {new Date(selectedOrder.keeta_delivery_info?.cancel_info?.cancelled_at || selectedOrder.cancelled_at).toLocaleString("pt-BR")}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Desconto/Cupom Aplicado - KEETA com detalhes */}
              {selectedOrder.discount_details && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-xs text-purple-700 uppercase font-semibold mb-2 flex items-center gap-1">
                    <Tag size={14} />
                    Descontos Aplicados
                  </p>
                  <div className="space-y-2">
                    {/* Códigos de cupom */}
                    {selectedOrder.discount_details.coupon_codes && selectedOrder.discount_details.coupon_codes.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-purple-700">Cupom:</span>
                        {selectedOrder.discount_details.coupon_codes.map((code, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-purple-200 text-purple-800 rounded text-xs font-mono">
                            {code}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Contribuições separadas */}
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-purple-200">
                      {selectedOrder.discount_details.merchant_contribution > 0 && (
                        <div className="bg-white p-2 rounded border border-purple-100">
                          <p className="text-xs text-slate-500">Loja</p>
                          <p className="font-bold text-purple-700">
                            -{formatCurrency(selectedOrder.discount_details.merchant_contribution)}
                          </p>
                        </div>
                      )}
                      {selectedOrder.discount_details.marketplace_contribution > 0 && (
                        <div className="bg-white p-2 rounded border border-purple-100">
                          <p className="text-xs text-slate-500">Keeta</p>
                          <p className="font-bold text-purple-700">
                            -{formatCurrency(selectedOrder.discount_details.marketplace_contribution)}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Total de desconto */}
                    <div className="pt-2 border-t border-purple-200 flex justify-between items-center">
                      <span className="text-sm font-medium text-purple-700">Total de Desconto:</span>
                      <span className="font-bold text-lg text-purple-900">
                        -{formatCurrency(selectedOrder.discount_details.total || selectedOrder.discount)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Desconto/Cupom Aplicado - 99Food e outros canais (legado) */}
              {!selectedOrder.discount_details && selectedOrder.coupon_value && selectedOrder.coupon_value > 0 && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-xs text-purple-700 uppercase font-semibold mb-2 flex items-center gap-1">
                    <Tag size={14} />
                    Desconto Aplicado
                  </p>
                  <div className="space-y-1">
                    <p className="text-sm">
                      <span className="font-medium text-purple-700">Valor:</span> 
                      <span className="font-bold text-purple-900 ml-1">-{formatCurrency(selectedOrder.coupon_value)}</span>
                    </p>
                    <p className="text-sm">
                      <span className="font-medium text-purple-700">Origem:</span> 
                      <span className={"ml-1 px-2 py-0.5 rounded text-xs font-bold " + (selectedOrder.coupon_source === "99FOOD" ? "bg-orange-100 text-orange-700" : "bg-green-100 text-green-700")}>
                        {selectedOrder.coupon_source || "N/A"}
                      </span>
                    </p>
                    {selectedOrder.coupon_description && (
                      <p className="text-sm text-purple-600">
                        <span className="font-medium">Descrição:</span> {selectedOrder.coupon_description}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {selectedOrder.delivery_info && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-xs text-blue-700 uppercase font-semibold mb-2 flex items-center gap-1">
                    <Truck size={14} />
                    Informações de Entrega
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-blue-600 text-xs">Endereço</p>
                      {selectedOrder.external_source === 'KEETA' ? (
                        selectedOrder.keeta_delivery_info?.is_self_delivery ? (
                          // Self-delivery: Mostrar endereço completo formatado pela Keeta
                          selectedOrder.keeta_delivery_info?.address?.formattedAddress && 
                          !selectedOrder.keeta_delivery_info?.address?.formattedAddress.startsWith('ENC_') &&
                          selectedOrder.keeta_delivery_info?.address?.formattedAddress !== 'Proteção de Privacidade' ? (
                            <p className="text-sm">{selectedOrder.keeta_delivery_info.address.formattedAddress}</p>
                          ) : selectedOrder.keeta_delivery_info?.address?.street && 
                              !selectedOrder.keeta_delivery_info?.address?.street.startsWith('ENC_') ? (
                            // Fallback: montar endereço com street, number e city
                            <p className="text-sm">
                              {selectedOrder.keeta_delivery_info.address.street}
                              {selectedOrder.keeta_delivery_info.address.number && `, ${selectedOrder.keeta_delivery_info.address.number}`}
                              {selectedOrder.keeta_delivery_info.address.city && `, ${selectedOrder.keeta_delivery_info.address.city}`}
                            </p>
                          ) : (
                            <p className="text-amber-600 text-xs italic">
                              Endereço criptografado - aguardando descriptografia
                            </p>
                          )
                        ) : (
                          // Marketplace: Endereço gerenciado pela Keeta
                          <p className="text-amber-600 text-xs italic">
                            Entrega pelo KEETA - Endereço gerenciado pela plataforma
                          </p>
                        )
                      ) : ['IFOOD', '99FOOD'].includes(selectedOrder.external_source) && 
                         (!selectedOrder.delivery_info.address_street || selectedOrder.delivery_info.address_street === 'Proteção de Privacidade') ? (
                        <p className="text-amber-600 text-xs italic">
                          Entrega pelo {selectedOrder.external_source} - Endereço gerenciado pela plataforma
                        </p>
                      ) : (
                        <>
                          <p>{selectedOrder.delivery_info.address_street}, {selectedOrder.delivery_info.address_number}{selectedOrder.delivery_info.address_complement ? ` - ${selectedOrder.delivery_info.address_complement}` : ''}</p>
                          <p className="text-xs text-slate-500">{selectedOrder.delivery_info.address_neighborhood}</p>
                        </>
                      )}
                    </div>
                    <div>
                      <p className="text-blue-600 text-xs">Tipo de Entrega</p>
                      <p>
                        {selectedOrder.external_source === 'KEETA' 
                          ? (selectedOrder.keeta_delivery_info?.is_self_delivery 
                              ? 'Entrega Própria (Self-Delivery Keeta)'
                              : 'Entrega Keeta (Marketplace)')
                          : selectedOrder.external_source === 'IFOOD'
                            ? (selectedOrder.delivery_info?.delivery_type === 'LOJA' ? 'Entrega pelo Restaurante' : 'Entrega iFood')
                            : selectedOrder.external_source === '99FOOD'
                              ? (selectedOrder.delivery_info?.delivery_type === 'LOJA' ? 'Entrega da Loja' : 'Entrega 99Food (Marketplace)')
                              : selectedOrder.delivery_info.delivery_type === 'LOJA' 
                                ? 'Entrega Própria' 
                                : selectedOrder.delivery_info.delivery_type}
                      </p>
                    </div>
                    {selectedOrder.delivery_info.driver_name && (
                      <div>
                        <p className="text-blue-600 text-xs">Entregador</p>
                        <p>{selectedOrder.delivery_info.driver_name}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-blue-600 text-xs">Status Entrega</p>
                      <p>{selectedOrder.delivery_info.dispatcher_status}</p>
                    </div>
                  </div>
                  {/* iFood Logistics assignDriver — banner de alerta */}
                  {selectedOrder.delivery_info.logistics_sync_status === 'FAILED' && (
                    <div className="mt-3 px-3 py-2 rounded-md border bg-amber-50 border-amber-300 text-amber-800" data-testid="logistics-sync-failed-banner">
                      <div className="text-sm font-semibold flex items-center gap-2">
                        <span>⚠</span>
                        <span>iFood Logistics não confirmou atribuição</span>
                      </div>
                      {selectedOrder.delivery_info.logistics_sync_error && (
                        <div className="text-xs mt-1 opacity-90 break-words">
                          {selectedOrder.delivery_info.logistics_sync_error}
                        </div>
                      )}
                      {selectedOrder.delivery_info.logistics_sync_at && (
                        <div className="text-[10px] mt-1 opacity-70">
                          Tentado em {new Date(selectedOrder.delivery_info.logistics_sync_at).toLocaleString('pt-BR')}
                        </div>
                      )}
                    </div>
                  )}
                  {selectedOrder.delivery_info.logistics_sync_status === 'OK' && (
                    <div className="mt-3 px-3 py-1.5 rounded-md border bg-green-50 border-green-200 text-green-800 text-xs flex items-center gap-2" data-testid="logistics-sync-ok-banner">
                      <span>✓</span>
                      <span>iFood Logistics confirmou atribuição</span>
                    </div>
                  )}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-500 uppercase font-semibold">Itens</p>
                  {canEdit(selectedOrder) && !editMode && (
                    <button
                      data-testid="btn-editar-pedido"
                      onClick={startEdit}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      <Edit3 size={14} /> Editar Pedido
                    </button>
                  )}
                </div>
                
                {/* MODO LEITURA */}
                {!editMode && (
                  <div className="bg-slate-50 rounded-lg divide-y divide-slate-200">
                    {selectedOrder.items?.map((item, idx) => {
                      // Auditoria de cálculo: derivar base + adicionais + subtotal
                      const qty = Number(item.quantity || 1);
                      const totalLine = Number(item.total_price || 0);
                      const modsSum = (item.modifiers || []).reduce(
                        (acc, m) => acc + Number(m.price_modifier ?? m.unit_price ?? 0), 0
                      );
                      // total_price já inclui modifiers; total da linha = qty * (base + mods)
                      // base unitário = (total_price / qty) - modsSum
                      const unitWithMods = qty > 0 ? totalLine / qty : totalLine;
                      const basePrice = Math.max(0, unitWithMods - modsSum);
                      const hasMods = (item.modifiers || []).length > 0;
                      return (
                        <div key={idx} className="p-3" data-testid={`order-item-${idx}`}>
                          <div className="flex justify-between items-start">
                            <p className="font-medium">{qty}x {item.product_name}</p>
                            <p className="font-semibold" data-testid={`order-item-subtotal-${idx}`}>
                              {formatCurrency(totalLine)}
                            </p>
                          </div>
                          {hasMods && (
                            <div className="mt-2 ml-1 pl-3 border-l-2 border-emerald-200 space-y-1 text-xs">
                              <div className="flex justify-between text-slate-500">
                                <span>Valor base do produto</span>
                                <span data-testid={`order-item-base-${idx}`}>
                                  {formatCurrency(basePrice)}
                                </span>
                              </div>
                              {item.modifiers.map((mod, midx) => {
                                const modPrice = Number(mod.price_modifier ?? mod.unit_price ?? 0);
                                return (
                                  <div key={midx} className="flex justify-between text-emerald-700">
                                    <span>+ {mod.option_name || mod.name}</span>
                                    <span>+{formatCurrency(modPrice)}</span>
                                  </div>
                                );
                              })}
                              {qty > 1 && (
                                <div className="flex justify-between text-slate-400">
                                  <span>× {qty} un.</span>
                                  <span></span>
                                </div>
                              )}
                              <div className="flex justify-between border-t border-emerald-200 pt-1 mt-1 font-medium text-slate-700">
                                <span>Subtotal do item</span>
                                <span>{formatCurrency(totalLine)}</span>
                              </div>
                            </div>
                          )}
                          {item.notes && (
                            <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-2">
                              Obs: {item.notes}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* MODO EDICAO */}
                {editMode && (
                  <div className="space-y-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg divide-y divide-blue-100">
                      {editItems.map((item, idx) => (
                        item.quantity > 0 && (
                          <div key={idx} className="p-3 flex justify-between items-center" data-testid={`edit-item-${idx}`}>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{item.product_name}</p>
                              <p className="text-xs text-slate-500">{formatCurrency(item.unit_price)} un.</p>
                              {item.stock_deducted && (
                                <p className="text-xs text-amber-600 font-medium mt-0.5">Estoque baixado</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-3">
                              <button
                                data-testid={`btn-minus-${idx}`}
                                disabled={item.stock_deducted || item.quantity <= 1}
                                onClick={() => updateItemQty(idx, -1)}
                                className="w-7 h-7 rounded-full bg-white border border-slate-300 flex items-center justify-center hover:bg-slate-100 disabled:opacity-30"
                              >
                                <Minus size={14} />
                              </button>
                              <span className="w-6 text-center font-bold text-sm">{item.quantity}</span>
                              <button
                                data-testid={`btn-plus-${idx}`}
                                disabled={item.stock_deducted}
                                onClick={() => updateItemQty(idx, 1)}
                                className="w-7 h-7 rounded-full bg-white border border-slate-300 flex items-center justify-center hover:bg-slate-100 disabled:opacity-30"
                              >
                                <Plus size={14} />
                              </button>
                              <button
                                data-testid={`btn-remove-${idx}`}
                                disabled={item.stock_deducted}
                                onClick={() => removeItem(idx)}
                                className="w-7 h-7 rounded-full bg-red-50 border border-red-200 flex items-center justify-center hover:bg-red-100 text-red-500 disabled:opacity-30 ml-1"
                              >
                                <Trash2 size={14} />
                              </button>
                              <span className="w-16 text-right font-semibold text-sm">{formatCurrency(item.total_price)}</span>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                    
                    <button
                      data-testid="btn-adicionar-item"
                      onClick={openAddItem}
                      className="w-full py-2 border-2 border-dashed border-blue-300 rounded-lg text-blue-600 text-sm font-medium hover:bg-blue-50 flex items-center justify-center gap-1"
                    >
                      <Plus size={16} /> Adicionar Item
                    </button>
                    
                    <div className="bg-slate-100 rounded-lg p-3 mt-2">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal</span>
                        <span className="font-medium">{formatCurrency(editSubtotal)}</span>
                      </div>
                      {(selectedOrder.delivery_fee || 0) > 0 && (
                        <div className="flex justify-between text-sm text-slate-500">
                          <span>Taxa entrega</span>
                          <span>{formatCurrency(selectedOrder.delivery_fee)}</span>
                        </div>
                      )}
                      {(selectedOrder.discount || 0) > 0 && (
                        <div className="flex justify-between text-sm text-green-600">
                          <span>Desconto</span>
                          <span>-{formatCurrency(selectedOrder.discount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-base mt-1 pt-1 border-t border-slate-300">
                        <span>Total</span>
                        <span>{formatCurrency(editTotal)}</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-3">
                      <button
                        data-testid="btn-cancelar-edicao"
                        onClick={cancelEdit}
                        className="flex-1 py-2 px-4 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50"
                      >
                        Cancelar
                      </button>
                      <button
                        data-testid="btn-salvar-edicao"
                        onClick={saveEdit}
                        disabled={editSaving || editItems.filter(i => i.quantity > 0).length === 0}
                        className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                      >
                        {editSaving ? 'Salvando...' : 'Salvar Alterações'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {selectedOrder.notes && (
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Observações</p>
                  <div className="bg-yellow-50 p-3 rounded-lg text-sm space-y-1">
                    {selectedOrder.notes && !selectedOrder.notes.startsWith('{') && !selectedOrder.notes.includes('"metadata"') && <p>{selectedOrder.notes}</p>}
                  </div>
                </div>
              )}


              {/* 99Food Cancellation Pending Alert (P0) */}
              {false && selectedOrder.external_source === "99FOOD" && selectedOrder.food99_delivery_info?.cancellation_pending && ( // DESATIVADO - Contestação de cancelamento
                <div className="bg-red-50 p-4 rounded-lg border-2 border-red-300 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="text-red-600" size={20} />
                    <p className="text-red-600 font-bold uppercase">Solicitação de Cancelamento</p>
                  </div>
                  <div className="text-sm space-y-2 mb-4">
                    <p>
                      <span className="text-red-600 font-medium">Motivo: </span>
                      <span>{selectedOrder.food99_delivery_info.cancellation_request_info?.reason || "Não informado"}</span>
                    </p>
                    <p>
                      <span className="text-red-600 font-medium">Solicitado por: </span>
                      <span className="font-semibold">
                        {selectedOrder.food99_delivery_info.cancellation_request_info?.requested_by === "CUSTOMER" ? "Cliente" : 
                         selectedOrder.food99_delivery_info.cancellation_request_info?.requested_by === "SYSTEM" ? "Sistema" : "Loja"}
                      </span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleAcceptCancellation99Food(selectedOrder)}
                      disabled={cancelActionLoading}
                      className="flex-1"
                    >
                      {cancelActionLoading ? "Processando..." : "Aceitar Cancelamento"}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleDenyCancellation99Food(selectedOrder)}
                      disabled={cancelActionLoading}
                      className="flex-1 border-green-500 text-green-600 hover:bg-green-50"
                    >
                      {cancelActionLoading ? "Processando..." : "Negar Cancelamento"}
                    </Button>
                  </div>
                </div>
              )}
              {/* iFood Sync Status Banner */}
              {(selectedOrder.external_source === 'IFOOD' || selectedOrder.order_channel === 'IFOOD') && selectedOrder.ifood_details?.sync?.status && selectedOrder.ifood_details.sync.status !== 'OK' && (
                <div className="bg-red-100 p-3 rounded-lg border-2 border-red-400 flex items-start justify-between gap-3" data-testid="ifood-sync-warning">
                  <div className="flex-1">
                    <p className="text-xs font-bold text-red-800 uppercase mb-1">iFood nao sincronizado</p>
                    <p className="text-xs text-red-700">
                      {selectedOrder.ifood_details.sync.status === 'MERCHANT_MISMATCH'
                        ? 'Credencial nao tem acesso ao merchant deste pedido. Verifique vinculo no Portal do Parceiro iFood.'
                        : (selectedOrder.ifood_details.sync.error || `Falha: ${selectedOrder.ifood_details.sync.status}`)}
                    </p>
                    {selectedOrder.ifood_details.sync.action && (
                      <p className="text-[10px] text-red-600 mt-1">Acao: {selectedOrder.ifood_details.sync.action} | HTTP: {selectedOrder.ifood_details.sync.http || 'N/A'}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={"ifood-resync-btn-" + selectedOrder.id}
                    onClick={async () => {
                      try {
                        const res = await api.post(`/ifood/orders/${selectedOrder.id}/resync`);
                        if (res.data?.success) {
                          toast.success('Pedido sincronizado com iFood!');
                        } else {
                          toast.error(`Sync falhou: ${res.data?.error_code || res.data?.message || "erro"}`);
                        }
                        loadOrders();
                      } catch (e) {
                        toast.error('Erro ao resincronizar');
                      }
                    }}
                    className="border-red-500 text-red-700 hover:bg-red-50"
                  >
                    Reenviar
                  </Button>
                </div>
              )}
              {/* iFood Specific Details */}
              {(selectedOrder.external_source === 'IFOOD' || selectedOrder.order_channel === 'IFOOD') && selectedOrder.ifood_details && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-xs text-red-700 uppercase font-semibold mb-3 flex items-center gap-1">
                    <Package size={14} />
                    Informações iFood
                  </p>
                  <div className="space-y-3">
                    {/* Pagamento */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {selectedOrder.ifood_details.payment_method_info && (
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-red-600" />
                          <div>
                            <span className="text-red-600 text-xs">Pagamento</span>
                            <p className="font-medium">{selectedOrder.ifood_details.payment_method_info}</p>
                          </div>
                        </div>
                      )}
                      {selectedOrder.ifood_details.payment_type && (
                        <div>
                          <span className="text-red-600 text-xs">Tipo</span>
                          <p className="font-medium">{selectedOrder.ifood_details.payment_type === 'ONLINE' ? 'Pago Online' : 'Na Entrega'}</p>
                        </div>
                      )}
                      {selectedOrder.ifood_details.card_brand && (
                        <div>
                          <span className="text-red-600 text-xs">Bandeira</span>
                          <p className="font-medium">{selectedOrder.ifood_details.card_brand}</p>
                        </div>
                      )}
                      {selectedOrder.ifood_details.change_for && selectedOrder.ifood_details.change_for > 0 && (
                        <div className="col-span-2 bg-white p-2 rounded border border-red-100">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-red-600" />
                            <div>
                              <span className="text-red-600 text-xs">Troco para</span>
                              <p className="font-bold">{formatCurrency(selectedOrder.ifood_details.change_for)}</p>
                              <p className="text-xs text-slate-500">Troco: {formatCurrency(selectedOrder.ifood_details.change_for - selectedOrder.total)}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Código de Coleta e CPF */}
                    <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-red-200">
                      {selectedOrder.ifood_details.pickup_code && (
                        <div className="bg-white p-2 rounded border border-red-100">
                          <span className="text-red-600 text-xs">Código de Coleta</span>
                          <p className="font-mono font-bold text-lg text-red-700">{selectedOrder.ifood_details.pickup_code}</p>
                        </div>
                      )}
                      {selectedOrder.ifood_details.customer_document && (
                        <div className="bg-white p-2 rounded border border-red-100">
                          <span className="text-red-600 text-xs">{selectedOrder.ifood_details.customer_document_type || 'CPF'}</span>
                          <p className="font-medium">{selectedOrder.ifood_details.customer_document}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Benefícios/Cupons com detalhamento de subsídio */}
                    {selectedOrder.ifood_details.benefits && selectedOrder.ifood_details.benefits.length > 0 && (
                      <div className="pt-2 border-t border-red-200">
                        <p className="text-red-700 text-xs font-semibold mb-2 flex items-center gap-1">
                          <Tag size={12} />
                          Descontos / Cupons
                        </p>
                        <div className="space-y-2">
                          {selectedOrder.ifood_details.benefits.map((benefit, idx) => (
                            <div key={idx} className="bg-white p-2 rounded border border-red-100">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium">
                                  {formatCurrency(benefit.value || 0)} — {benefit.target || 'CART'}
                                </span>
                              </div>
                              {/* Detalhamento de subsídio por patrocinador */}
                              {benefit.sponsorshipValues && benefit.sponsorshipValues.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-2">
                                  {benefit.sponsorshipValues.map((sponsor, sidx) => (
                                    <span 
                                      key={sidx} 
                                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        sponsor.name === 'IFOOD' ? 'bg-red-100 text-red-700' :
                                        sponsor.name === 'MERCHANT' ? 'bg-orange-100 text-orange-700' :
                                        sponsor.name === 'EXTERNAL' ? 'bg-blue-100 text-blue-700' :
                                        sponsor.name === 'CHAIN' ? 'bg-purple-100 text-purple-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      {sponsor.name}: {formatCurrency(sponsor.value || 0)}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                          {/* Totalizador de subsídios */}
                          {(() => {
                            const totals = { IFOOD: 0, MERCHANT: 0, EXTERNAL: 0, CHAIN: 0 };
                            selectedOrder.ifood_details.benefits.forEach(b => {
                              (b.sponsorshipValues || []).forEach(s => {
                                if (totals.hasOwnProperty(s.name)) totals[s.name] += (s.value || 0);
                              });
                            });
                            const hasValues = Object.values(totals).some(v => v > 0);
                            return hasValues ? (
                              <div className="mt-2 pt-2 border-t border-red-100 grid grid-cols-2 sm:grid-cols-4 gap-1 text-center text-xs">
                                <div className={totals.IFOOD > 0 ? 'bg-red-100 p-1 rounded' : 'text-slate-300'}>
                                  <p className="font-semibold">IFOOD</p>
                                  <p>{formatCurrency(totals.IFOOD)}</p>
                                </div>
                                <div className={totals.MERCHANT > 0 ? 'bg-orange-100 p-1 rounded' : 'text-slate-300'}>
                                  <p className="font-semibold">LOJA</p>
                                  <p>{formatCurrency(totals.MERCHANT)}</p>
                                </div>
                                <div className={totals.EXTERNAL > 0 ? 'bg-blue-100 p-1 rounded' : 'text-slate-300'}>
                                  <p className="font-semibold">EXTERNO</p>
                                  <p>{formatCurrency(totals.EXTERNAL)}</p>
                                </div>
                                <div className={totals.CHAIN > 0 ? 'bg-purple-100 p-1 rounded' : 'text-slate-300'}>
                                  <p className="font-semibold">REDE</p>
                                  <p>{formatCurrency(totals.CHAIN)}</p>
                                </div>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    )}
                    
                    {/* Observações de entrega iFood */}
                    {selectedOrder.ifood_details.delivery_observations && (
                      <div className="pt-2 border-t border-red-200">
                        <p className="text-red-600 text-xs">Observações de Entrega</p>
                        <p className="text-sm bg-white p-2 rounded mt-1">{selectedOrder.ifood_details.delivery_observations}</p>
                      </div>
                    )}
                    
                    {/* Observações dos itens */}
                    {selectedOrder.ifood_details.item_observations && selectedOrder.ifood_details.item_observations.length > 0 && (
                      <div className="pt-2 border-t border-red-200">
                        <p className="text-red-600 text-xs mb-1">Observações dos Itens</p>
                        <div className="space-y-1">
                          {selectedOrder.ifood_details.item_observations.map((obs, idx) => (
                            <p key={idx} className="text-sm bg-white p-2 rounded">
                              <span className="font-medium">{obs.name}:</span> {obs.observation}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Agendamento */}
                    {selectedOrder.ifood_details.scheduled_datetime && (
                      <div className="pt-2 border-t border-red-200 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-600" />
                        <div>
                          <span className="text-amber-600 text-xs">Pedido Agendado para</span>
                          <p className="font-medium text-amber-700">{new Date(selectedOrder.ifood_details.scheduled_datetime).toLocaleString('pt-BR')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 99Food Extra Info (P0) */}
              {selectedOrder.external_source === '99FOOD' && selectedOrder.food99_extra_info && (
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <p className="text-xs text-orange-600 uppercase font-semibold mb-2">Informações 99Food</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {selectedOrder.food99_extra_info.localizador && (
                      <div>
                        <span className="text-orange-600 font-medium">Localizador: </span>
                        <span className="font-bold">{selectedOrder.food99_extra_info.localizador}</span>
                      </div>
                    )}
                    {selectedOrder.food99_extra_info.precisa_de_talhares && (
                      <div>
                        <span className="text-orange-600 font-medium">Talheres: </span>
                        <span>{selectedOrder.food99_extra_info.precisa_de_talhares === "sim" ? "Sim" : "Não"}</span>
                      </div>
                    )}
                    {selectedOrder.food99_extra_info.tracking_url && (
                      <div className="col-span-2">
                        <a href={selectedOrder.food99_extra_info.tracking_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">
                          Acompanhar Entrega
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 99Food Cancel Info (P1) */}
              {selectedOrder.external_source === '99FOOD' && selectedOrder.food99_delivery_info?.cancel_info && (
                <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                  <p className="text-xs text-red-600 uppercase font-semibold mb-2">Informações de Cancelamento 99Food</p>
                  <div className="space-y-1 text-sm">
                    {selectedOrder.food99_delivery_info.cancel_info.cancelled_by && (
                      <p><span className="text-red-600">Cancelado por: </span>{selectedOrder.food99_delivery_info.cancel_info.cancelled_by}</p>
                    )}
                    {selectedOrder.food99_delivery_info.cancel_info.reason && (
                      <p><span className="text-red-600">Motivo: </span>{selectedOrder.food99_delivery_info.cancel_info.reason}</p>
                    )}
                    {selectedOrder.food99_delivery_info.cancel_info.code && (
                      <p><span className="text-red-600">Código: </span>{selectedOrder.food99_delivery_info.cancel_info.code}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Forma de Pagamento (genérico - todos os canais) */}
              {selectedOrder.payment_mode && !(selectedOrder.payment_mode === 'COD' && selectedOrder.origin === 'CARDAPIO_DIGITAL') && (
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200" data-testid="payment-info-block">
                  <p className="text-xs text-emerald-700 uppercase font-semibold mb-2 flex items-center gap-1">
                    <CreditCard size={14} />
                    Forma de Pagamento
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-emerald-600 text-xs">Tipo</span>
                      <p className="font-medium">
                        {selectedOrder.payment_mode === 'ONLINE' ? 'Pago Online' :
                         selectedOrder.payment_mode === 'COD' ? 'Pagamento na Entrega' :
                         selectedOrder.payment_mode === 'POS_STORE' ? 'Pago no Balcão' :
                         selectedOrder.payment_mode}
                      </p>
                    </div>
                    <div>
                      <span className="text-emerald-600 text-xs">Status</span>
                      <p className={`font-bold ${selectedOrder.payment_status === 'PAGO' || selectedOrder.payment_status === 'APPROVED' ? 'text-green-700' : 'text-orange-600'}`}>
                        {selectedOrder.payment_status === 'PAGO' || selectedOrder.payment_status === 'APPROVED' ? 'Pago' :
                         selectedOrder.payment_status === 'PENDENTE' || selectedOrder.payment_status === 'UNPAID' ? 'Pendente' :
                         selectedOrder.payment_status || 'N/A'}
                      </p>
                    </div>
                    {selectedOrder.payment_mode === 'ONLINE' && (
                      <div>
                        <span className="text-emerald-600 text-xs">Gateway</span>
                        <p className="font-medium">
                          {selectedOrder.external_source === '99FOOD' ? 'Pagamento via 99Food' :
                           selectedOrder.external_source === 'IFOOD' ? 'Pagamento via iFood' :
                           selectedOrder.external_source === 'KEETA' ? 'Pagamento via Keeta' :
                           'Mercado Pago'}
                        </p>
                      </div>
                    )}
                    {selectedOrder.payment_mode === 'COD' && selectedOrder.cod_status && (
                      <div>
                        <span className="text-emerald-600 text-xs">Status COD</span>
                        <p className={`font-bold ${
                          selectedOrder.cod_status === 'COLLECTED' ? 'text-green-700' :
                          selectedOrder.cod_status === 'EXPECTED' ? 'text-amber-600' :
                          selectedOrder.cod_status === 'NONE' ? 'text-slate-400' :
                          'text-slate-600'
                        }`}>
                          {selectedOrder.cod_status === 'EXPECTED' ? 'Aguardando Coleta' :
                           selectedOrder.cod_status === 'COLLECTED' ? 'Coletado' :
                           selectedOrder.cod_status === 'NONE' ? 'N/A' :
                           selectedOrder.cod_status}
                        </p>
                      </div>
                    )}
                    {selectedOrder.payment_mode === 'COD' && selectedOrder.cod_expected_amount > 0 && (
                      <div>
                        <span className="text-emerald-600 text-xs">Valor Esperado</span>
                        <p className="font-bold text-amber-700">{formatCurrency(selectedOrder.cod_expected_amount)}</p>
                      </div>
                    )}
                    {selectedOrder.payment_mode === 'COD' && selectedOrder.cod_collected_amount > 0 && (
                      <div>
                        <span className="text-emerald-600 text-xs">Valor Coletado</span>
                        <p className="font-bold text-green-700">{formatCurrency(selectedOrder.cod_collected_amount)}</p>
                      </div>
                    )}
                    {/* Admin: Baixa manual COD para pedidos pendentes */}
                    {selectedOrder.payment_mode === 'COD' && 
                     selectedOrder.cod_status !== 'COLLECTED' && 
                     selectedOrder.payment_status !== 'PAGO' &&
                     selectedOrder.payment_status !== 'PAID' &&
                     selectedOrder.payment_status !== 'APPROVED' && (
                      <div className="col-span-2 mt-2">
                        <AdminCodSettleButton 
                          orderId={selectedOrder.id} 
                          total={selectedOrder.total}
                          onSettled={() => loadOrders()}
                        />
                      </div>
                    )}
                    {/* Valor pago pela plataforma (marketplaces) */}
                    {selectedOrder.payment_mode === 'ONLINE' && 
                     (selectedOrder.external_source === '99FOOD' || selectedOrder.external_source === 'IFOOD' || selectedOrder.external_source === 'KEETA') &&
                     selectedOrder.payment_status === 'PAGO' && (
                      <div className="col-span-2 bg-white p-2 rounded border border-emerald-100">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-emerald-600" />
                          <div>
                            <span className="text-emerald-600 text-xs">Pago pela Plataforma</span>
                            <p className="font-bold text-green-700">{formatCurrency(selectedOrder.total)}</p>
                            <p className="text-xs text-slate-500">Cobrar do cliente: R$ 0,00</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedOrder.payment_change > 0 && (
                      <div className="col-span-2 bg-white p-2 rounded border border-emerald-100">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-emerald-600" />
                          <div>
                            <span className="text-emerald-600 text-xs">Troco para</span>
                            <p className="font-bold">{formatCurrency(selectedOrder.payment_change)}</p>
                            <p className="text-xs text-slate-500">Troco: {formatCurrency(selectedOrder.payment_change - selectedOrder.total)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* AJUSTE FINANCEIRO */}
              {adjustments.length > 0 && !editMode && (
                <div className="mt-3">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-2">Ajustes Financeiros</p>
                  <div className="space-y-2">
                    {adjustments.map((adj) => (
                      <div 
                        key={adj.id}
                        data-testid={"adjustment-" + adj.id}
                        className={"p-3 rounded-lg border " + (
                          adj.status === 'PENDING' 
                            ? 'bg-amber-50 border-amber-300' 
                            : adj.status === 'RESOLVED'
                              ? 'bg-green-50 border-green-200'
                              : 'bg-slate-50 border-slate-200'
                        )}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <AlertCircle size={14} className={adj.status === 'PENDING' ? 'text-amber-600' : 'text-green-600'} />
                              <span className={"text-xs font-bold uppercase " + (
                                adj.status === 'PENDING' ? 'text-amber-700' : adj.status === 'RESOLVED' ? 'text-green-700' : 'text-slate-500'
                              )}>
                                {adj.status === 'PENDING' ? 'Ajuste Pendente' : adj.status === 'RESOLVED' ? 'Resolvido' : 'Ignorado'}
                              </span>
                            </div>
                            <p className={"text-lg font-bold " + (adj.difference > 0 ? 'text-red-600' : 'text-blue-600')}>
                              {adj.difference > 0 ? '+' : ''}{formatCurrency(adj.difference)}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {adj.adjustment_type === 'INCREASE' 
                                ? 'Cliente deve pagar diferenca' 
                                : 'Cliente pagou a mais'}
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              De {formatCurrency(adj.old_total)} para {formatCurrency(adj.new_total)}
                            </p>
                          </div>
                          {adj.status === 'PENDING' && (
                            <div className="flex gap-1">
                              <button
                                data-testid={"btn-resolve-" + adj.id}
                                onClick={() => resolveAdjustment(adj.id, 'RESOLVED')}
                                className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                              >
                                Resolvido
                              </button>
                              <button
                                data-testid={"btn-ignore-" + adj.id}
                                onClick={() => resolveAdjustment(adj.id, 'IGNORED')}
                                className="px-2 py-1 text-xs bg-slate-400 text-white rounded hover:bg-slate-500 font-medium"
                              >
                                Ignorar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal</span>
                  <span>{formatCurrency(selectedOrder.subtotal)}</span>
                </div>
                {/* Taxa de Entrega - Usar keeta_delivery_info.fees para pedidos Keeta */}
                {(selectedOrder.external_source === 'KEETA' && selectedOrder.keeta_delivery_info?.fees?.delivery_fee > 0) ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Taxa de Entrega</span>
                    <span>{formatCurrency(selectedOrder.keeta_delivery_info.fees.delivery_fee)}</span>
                  </div>
                ) : selectedOrder.delivery_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Taxa de Entrega</span>
                    <span>{formatCurrency(selectedOrder.delivery_fee)}</span>
                  </div>
                )}
                {/* Taxa de Serviço - Usar keeta_delivery_info.fees para pedidos Keeta */}
                {(selectedOrder.external_source === 'KEETA' && selectedOrder.keeta_delivery_info?.fees?.service_fee > 0) ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Taxa de Serviço</span>
                    <span>{formatCurrency(selectedOrder.keeta_delivery_info.fees.service_fee)}</span>
                  </div>
                ) : (selectedOrder.service_fee > 0 && selectedOrder.external_source !== '99FOOD') && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Taxa de Serviço</span>
                    <span>{formatCurrency(selectedOrder.service_fee)}</span>
                  </div>
                )}
                {/* Taxa de Pedido Mínimo - Apenas para Keeta */}
                {selectedOrder.external_source === 'KEETA' && selectedOrder.keeta_delivery_info?.fees?.min_order_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Taxa Pedido Mínimo</span>
                    <span>{formatCurrency(selectedOrder.keeta_delivery_info.fees.min_order_fee)}</span>
                  </div>
                )}
                {selectedOrder.discount > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>{selectedOrder.external_source === '99FOOD' ? 'Entrega Promocional' :
                           selectedOrder.coupon_description ? selectedOrder.coupon_description :
                           'Desconto'}</span>
                    <span>-{formatCurrency(selectedOrder.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total</span>
                  <span className="text-emerald-600">{formatCurrency(selectedOrder.total)}</span>
                </div>
                {selectedOrder.external_source === '99FOOD' && selectedOrder.service_fee > 0 && (
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>Taxa de Serviço 99Food (cobrada do cliente)</span>
                    <span>{formatCurrency(selectedOrder.service_fee)}</span>
                  </div>
                )}
                {/* Comissao Estimada 99Food - APENAS VISUAL, taxa lida da API */}
                {selectedOrder.external_source === '99FOOD' && selectedOrder.total > 0 && commissionRate99Food > 0 && (() => {
                  const bruto = selectedOrder.total;
                  const comissao = Math.round(bruto * (commissionRate99Food / 100) * 100) / 100;
                  const repasse = Math.round((bruto - comissao) * 100) / 100;
                  return (
                    <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg" data-testid="99food-commission-estimate">
                      <div className="flex justify-between text-sm">
                        <span className="text-orange-700">Comissao estimada 99Food (~{commissionRate99Food.toFixed(2).replace('.', ',')}%)</span>
                        <span className="text-orange-700 font-medium">-{formatCurrency(comissao)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-bold mt-1">
                        <span className="text-emerald-700">Repasse estimado</span>
                        <span className="text-emerald-700">~{formatCurrency(repasse)}</span>
                      </div>
                      <p className="text-[10px] text-orange-500 mt-1 italic">Estimativa baseada na taxa configurada da 99Food</p>
                    </div>
                  );
                })()}
                {selectedOrder.external_source === '99FOOD' && selectedOrder.total > 0 && !commissionRate99Food && (
                  <p className="text-[10px] text-slate-400 mt-1 italic" data-testid="99food-commission-not-configured">Taxa estimada 99Food nao configurada</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Adicionar Item */}
      <Dialog open={addItemModalOpen} onOpenChange={setAddItemModalOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Item ao Pedido</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                data-testid="add-item-search"
                type="text"
                placeholder="Buscar produto..."
                value={addItemSearch}
                onChange={e => setAddItemSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {productsLoading ? (
              <p className="text-center text-slate-500 py-4">Carregando...</p>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[50vh] overflow-y-auto">
                {productsList
                  .filter(p => !addItemSearch || p.name.toLowerCase().includes(addItemSearch.toLowerCase()))
                  .map(product => (
                    <button
                      key={product.id}
                      data-testid={`add-product-${product.id}`}
                      onClick={() => addProduct(product)}
                      className="w-full p-3 flex justify-between items-center hover:bg-blue-50 text-left transition-colors"
                    >
                      <div>
                        <p className="font-medium text-sm">{product.name}</p>
                        <p className="text-xs text-slate-500">{product.category_name || ''}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{formatCurrency(product.price)}</span>
                        <Plus size={16} className="text-blue-600" />
                      </div>
                    </button>
                  ))
                }
                {productsList.filter(p => !addItemSearch || p.name.toLowerCase().includes(addItemSearch.toLowerCase())).length === 0 && (
                  <p className="text-center text-slate-400 py-4 text-sm">Nenhum produto encontrado</p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    
      {/* Modal de Cancelamento */}
      {/* ==================== MODAL COD PAYMENT ==================== */}
      {codModalOpen && codModalOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="cod-payment-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Confirmar Pagamento COD</h3>
            <p className="text-sm text-gray-500 mb-4">
              Pedido <span className="font-bold">#{codModalOrder.order_number}</span> - {codModalOrder.customer_name}
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-amber-800 text-sm font-medium">Pagamento na entrega ainda nao confirmado.</p>
              <p className="text-amber-700 text-xl font-bold mt-1">R$ {parseFloat(codModalOrder.total).toFixed(2)}</p>
            </div>

            {!codOverrideMode ? (
              <>
                <div className="space-y-3 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento</label>
                    <select value={codPaymentMethod} onChange={e => setCodPaymentMethod(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="cod-payment-method">
                      <option value="DINHEIRO">Dinheiro</option>
                      <option value="PIX">PIX</option>
                      <option value="CARTAO_CREDITO">Cartao Credito</option>
                      <option value="CARTAO_DEBITO">Cartao Debito</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor Recebido</label>
                    <input type="number" step="0.01" value={codAmount} onChange={e => setCodAmount(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="cod-amount" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleCodConfirmPayment} disabled={codProcessing}
                    className="flex-1 bg-emerald-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                    data-testid="cod-confirm-btn">
                    {codProcessing ? 'Processando...' : 'Confirmar Pagamento e Entregar'}
                  </button>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setCodOverrideMode(true)}
                    className="flex-1 text-xs text-gray-500 hover:text-orange-600 underline" data-testid="cod-override-toggle">
                    Entregar sem pagamento (excecao)
                  </button>
                  <button onClick={() => { setCodModalOpen(false); setCodModalOrder(null); }}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-red-700 text-sm font-medium">
                    Voce esta marcando como entregue SEM confirmar pagamento. Esta acao ficara registrada.
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Motivo da excecao *</label>
                  <textarea value={codOverrideReason} onChange={e => setCodOverrideReason(e.target.value)}
                    placeholder="Ex: Cliente vai pagar amanha, Pagamento via transferencia pendente..."
                    className="w-full border rounded-lg px-3 py-2 text-sm h-20" data-testid="cod-override-reason" />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleCodOverride} disabled={codProcessing || !codOverrideReason.trim()}
                    className="flex-1 bg-orange-500 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-orange-600 disabled:opacity-50"
                    data-testid="cod-override-confirm-btn">
                    {codProcessing ? 'Processando...' : 'Entregar sem Pagamento (Excecao)'}
                  </button>
                  <button onClick={() => setCodOverrideMode(false)}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Voltar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <XCircle size={20} />
              Cancelar Pedido
            </DialogTitle>
          </DialogHeader>

          {cancelOrderData && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">Pedido:</p>
                <p className="font-semibold">#{cancelOrderData.order_number || cancelOrderData.id?.slice(0, 8)}</p>
                <p className="text-xs text-slate-500 mt-1">Status atual: {cancelOrderData.status}</p>
              </div>

              {(cancelOrderData.status === "PREPARO" || cancelOrderData.status === "PRONTO") && (
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium flex items-center gap-1">
                    <AlertCircle size={14} />
                    Atenção: Produção já iniciada
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Este pedido será registrado como perda operacional (estoque não será devolvido).
                  </p>
                </div>
              )}

              {/* === SEGREGAÇÃO DE UI POR PLATAFORMA === */}
              
              {/* IFOOD - motivos carregados dinamicamente */}
              {cancelOrderData?.external_source === "IFOOD" && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Motivo do cancelamento iFood *</label>
                  {ifoodLoadingReasons ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-red-500" />
                    </div>
                  ) : !ifoodCanCancel ? (
                    <div className="text-center py-4 px-4 bg-red-50 rounded-lg">
                      <div className="w-12 h-12 rounded-full bg-red-100 mx-auto mb-3 flex items-center justify-center">
                        <XCircle className="w-6 h-6 text-red-500" />
                      </div>
                      <p className="text-sm text-red-600 font-medium">
                        {ifoodCancelMessage}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        O pedido pode já ter sido despachado ou estar em um estágio que não permite cancelamento pelo restaurante.
                      </p>
                    </div>
                  ) : ifoodCancelReasons.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {ifoodCancelReasons.map((reason) => (
                        <label
                          key={reason.cancelCodeId}
                          className={`flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                            selectedIfoodReason?.cancelCodeId === reason.cancelCodeId ? "border-red-500 bg-red-50" : "border-gray-200"
                          }`}
                        >
                          <input
                            type="radio"
                            name="ifoodCancelReason"
                            checked={selectedIfoodReason?.cancelCodeId === reason.cancelCodeId}
                            onChange={() => setSelectedIfoodReason(reason)}
                            className="mr-3"
                          />
                          <span className="text-sm">{reason.description || reason.cancelCodeId}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Carregando motivos do iFood...
                    </p>
                  )}
                </div>
              )}
              
              {/* KEETA - motivos oficiais da plataforma */}
              {cancelOrderData?.external_source === "KEETA" && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Motivo do cancelamento Keeta *</label>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {keetaCancelReasons.map((reason) => (
                      <label
                        key={reason.code}
                        className={`flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                          selectedKeetaReason?.code === reason.code ? "border-purple-500 bg-purple-50" : "border-gray-200"
                        }`}
                      >
                        <input
                          type="radio"
                          name="keetaCancelReason"
                          checked={selectedKeetaReason?.code === reason.code}
                          onChange={() => setSelectedKeetaReason(reason)}
                          className="mr-3"
                        />
                        <span className="text-sm">{reason.description}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 99FOOD - motivos específicos */}
              {cancelOrderData?.external_source === "99FOOD" && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Motivo do cancelamento 99Food *</label>
                  <select
                    className="w-full mt-1 border rounded-lg p-2 text-sm"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  >
                    <option value="">Selecione o motivo...</option>
                    <option value="UNAVAILABLE_ITEM">Item indisponível</option>
                    <option value="DUPLICATE_APPLICATION">Pedido duplicado</option>
                    <option value="RESTAURANT_WITHOUT_DELIVERY_PERSON">Restaurante sem entregador</option>
                    <option value="INTERNAL_DIFFICULTIES_OF_THE_RESTAURANT">Dificuldades internas do restaurante</option>
                    <option value="ORDER_OUTSIDE_THE_DELIVERY_AREA">Fora da área de entrega</option>
                    <option value="OUTSIDE_DELIVERY_HOURS">Fora do horário de entrega</option>
                    <option value="BLOCKED_CUSTOMER">Cliente bloqueado</option>
                    <option value="OUTDATED_MENU">Cardápio desatualizado</option>
                    <option value="DELIVERY_PROBLEM">Problema na entrega</option>
                    <option value="RISK_AREA">Área de risco</option>
                    <option value="SYSTEMIC_ISSUES">Problemas sistêmicos</option>
                  </select>
                </div>
              )}
              
              {/* FLUXO GENÉRICO - pedidos internos/locais */}
              {!cancelOrderData?.external_source || (cancelOrderData?.external_source !== "IFOOD" && cancelOrderData?.external_source !== "KEETA" && cancelOrderData?.external_source !== "99FOOD") ? (
                <>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Motivo do cancelamento *</label>
                    <select
                      className="w-full mt-1 border rounded-lg p-2 text-sm"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                    >
                      <option value="">Selecione o motivo...</option>
                      <option value="Cliente desistiu">Cliente desistiu</option>
                      <option value="Produto indisponível">Produto indisponível</option>
                      <option value="Erro no pedido">Erro no pedido</option>
                      <option value="Demora na entrega">Demora na entrega</option>
                      <option value="Duplicidade">Duplicidade</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>

                  {cancelReason === "Outro" && (
                    <div>
                      <label className="text-sm font-medium text-slate-700">Especifique o motivo</label>
                      <input
                        type="text"
                        className="w-full mt-1 border rounded-lg p-2 text-sm"
                        placeholder="Digite o motivo..."
                        onChange={(e) => setCancelReason(e.target.value)}
                      />
                    </div>
                  )}
                </>
              ) : null}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setCancelModalOpen(false)}
                  disabled={cancelLoading}
                >
                  Voltar
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleCancelOrder}
                  disabled={cancelLoading || (cancelOrderData?.external_source === "IFOOD" ? (!selectedIfoodReason || !ifoodCanCancel) : cancelOrderData?.external_source === "KEETA" ? !selectedKeetaReason : !cancelReason)}
                >
                  {cancelLoading ? (
                    <>
                      <Loader2 size={14} className="mr-1 animate-spin" />
                      Cancelando...
                    </>
                  ) : (
                    "Confirmar Cancelamento"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
// iFood details fix - forced rebuild 1774021824
